import React, { Suspense, useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback, startTransition } from 'react';
import DatePicker from '../common/datePicker/LazyDatePicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useDeferredMobileDateMonths } from './useDeferredMobileDateMonths';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Card,
  CardContent,
  Autocomplete,
  useTheme,
  Portal,
  useMediaQuery,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import flight_icon_homepage from '../../assets/airport-icon_dropdown.svg';
import swap_icon from '../../assets/swap_icon.svg';
import radioButtonFilled from '../../assets/Radio button_Filled.svg';
import radioButtonEmpty from '../../assets/Radio button_empty.svg';
import searchNormalIcon from '../../assets/search-normal.svg';
import { searchAirports } from '../../services/airportApi';
import MobileInteractionHost from './MobileInteractionHost';
import FareDateCalendar from './FareDateCalendar';
import { closeThenRun } from './fareDateGrid';
import { apiService } from '../../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSearchContext } from '../../context/SearchContext';
import { getSavedCurrency } from '../../utils/currency';
import { getUserLocation, findNearestAirportsToUser } from '../../services/airportApi';
import { ymdToLocalDate } from '../../utils/flightDateUtils';
import './styles/FlightForm.css';
import {
  FLIGHT_SEARCH_DATE_MIN_WIDTH,
  FLIGHT_SEARCH_FROM_FIELD_WIDTH,
  FLIGHT_SEARCH_STRIP_COLUMN_GAP,
  FLIGHT_SEARCH_STRIP_ROW_GAP,
  FLIGHT_SEARCH_TO_FIELD_WIDTH,
  getFlightSearchAirportFieldLayout,
  getFlightSearchDateFieldLayout,
  getFlightSearchDateInputSx,
  getFlightSearchStripPaddingRight,
  getFlightSearchTravelersFieldLayout,
  getFlightSearchWrappedTailLayout,
} from './flightSearchDateField';
import { MOBILE_HOMEPAGE_LAYOUT as L } from './mobileHomepageLayout';
import MobileSearchDateSheet from './MobileSearchDateSheet';
import { createGtmAttributes, GTM_CLICK_IDS, GTM_CONTEXT_ATTRS } from '../../analytics/gtmClickIds';
import ConvenienceFeeHomepageSearchBanner from '../promo/convenienceFee/ConvenienceFeeHomepageSearchBanner';
import { useHomepageSearchConvenienceFeePromo } from '../promo/convenienceFee/useHomepageSearchConvenienceFeePromo';
import { HOST_GROTESK_FAMILY } from '../../theme/tokens/typography';
import {
  MULTICITY_FROM_PLACEHOLDER, MULTICITY_TO_PLACEHOLDER,
  ORIGIN_PLACEHOLDER, DEST_PLACEHOLDER, TRAVEL_CLASS_LABELS, MOBILE_CLASS_OPTIONS,
  SPECIAL_FARE_META, SPECIAL_FARE_KEYS,
  emptyCalendarPrices,
} from './flightForm.constants';
import {
  formatStripDate, formatShortTripDate,
  buildAirportFilterResult,
  formatAirportStripDisplay, formatMobileSheetSelectedAirport,
} from './flightForm.utils';
import { fetchIndiaAirportDefaults, indiaDefaultsCache } from './indiaAirportDefaults';
import { wrapSpecialFarePill, SpecialFareInfoIcon } from './specialFarePills';
import { MOBILE_AIRPORT_SHEET_FIELD_SX } from './flightFormStyles';
import MountWhenOpened from '../common/MountWhenOpened';
import MultiCityRowsPlaceholder from './MultiCityRowsPlaceholder';

const loadMultiCityFormRow = () => import(/* webpackChunkName: "flight-multicity-row" */ './MultiCityFormRow');
const MultiCityFormRow = React.lazy(loadMultiCityFormRow);
const SpecialFareNoticeDialog = React.lazy(() =>
  import(/* webpackChunkName: "flight-special-fare-info" */ './SpecialFareNoticeDialog'),
);
const SpecialFareInfoSheet = React.lazy(() =>
  import(/* webpackChunkName: "flight-special-fare-info" */ './SpecialFareInfoSheet'),
);

/** @param {{ iata: string, name?: string, cityName?: string, countryName?: string, label?: string }} airport */
function buildAirportOptionFromInitial(airport) {
  if (!airport?.iata) return null;
  const cityName = airport.cityName || '';
  const countryName = airport.countryName || 'India';
  const name = airport.name || airport.iata;
  return {
    iataCode: airport.iata,
    value: airport.iata,
    name,
    label: airport.label || `${name} (${airport.iata}) - ${cityName}, ${countryName}`,
    cityName,
    countryName,
  };
}

function buildAirportInputLabel(airport) {
  if (!airport?.iata) return '';
  const cityName = airport.cityName || '';
  return cityName ? `${cityName} ${airport.iata}` : airport.iata;
}

function parseInitialCount(value, fallback) {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

const FlightForm = ({
  onStateChange,
  onAuthRequired,
  variant = 'default',
  initialSearch = null,
  initialAirports = null,
  disableGeolocation = false,
  enableCalendarFares = true,
  tripType: controlledTripType,
  onTripTypeChange,
  hideTripTypeSelector = false,
  journeySelector = null,
  /** SEO pages pass their desktop date/traveller fields so the homepage bundle never includes them. */
  seoHeroDesktopFields: SeoHeroDesktopFields = null,
}) => {
  const isEmbedded = variant === 'embedded';
  // Helper function to deduplicate airports by IATA code
  const deduplicateAirports = (airports) => {
    const seen = new Set();
    return airports.filter(airport => {
      if (seen.has(airport.iataCode)) {
        return false;
      }
      seen.add(airport.iataCode);
      return true;
    });
  };

  const theme = useTheme();
  /**
   * Aligns with MUI defaults: xs+sm = 0–899px (mobile/tablet), md+ = 900px+ (desktop).
   * From/To: bottom sheet + tap targets below md; MUI Autocomplete popper on md+.
   * Embedded SEO hero uses compact layout until lg so the form fits a narrow column.
   */
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('md'));
  const isEmbeddedNarrow = useMediaQuery(theme.breakpoints.down('lg'));
  const isMobile = isEmbedded ? isEmbeddedNarrow : isMobileViewport;
  /** SEO embed md+: FlightSelectHero-style date/traveller UX (matches hero visibility, not lg). */
  const isSeoHeroDesktopUx =
    isEmbedded && hideTripTypeSelector && !isMobileViewport;
  /** SEO multicity: only one dropdown/popup open at a time (segment:from|to|date, travelers, class). */
  const [seoMcActivePanel, setSeoMcActivePanel] = useState(null);
  const requestSeoMcPanel = useCallback((panelKey) => {
    setSeoMcActivePanel(panelKey);
  }, []);

  /** SEO embed only: class hook for special-fare active styling in flightSeoHeroForm.css */
  const isSeoEmbeddedSpecialFares = isEmbedded && hideTripTypeSelector;
  /** Desktop md+: main From/To are display-only comboboxes; typing uses in-dropdown search only. */
  const isDesktopFromSplit = useMediaQuery(theme.breakpoints.up('md'));

  // State variables
  const [internalTripType, setInternalTripType] = useState(
    () => initialSearch?.tripType || 'oneway',
  );
  const isTripTypeControlled =
    controlledTripType !== undefined && typeof onTripTypeChange === 'function';
  const tripType = isTripTypeControlled ? controlledTripType : internalTripType;
  const changeTripType = useCallback(
    (next) => {
      if (isTripTypeControlled) onTripTypeChange(next);
      else setInternalTripType(next);
    },
    [isTripTypeControlled, onTripTypeChange],
  );
  const [fromAirport, setFromAirport] = useState(() =>
    initialAirports?.from ? buildAirportOptionFromInitial(initialAirports.from) : null,
  );
  const fromAirportRef = useRef(null);
  useEffect(() => { fromAirportRef.current = fromAirport; }, [fromAirport]);
  const [toAirport, setToAirport] = useState(() =>
    initialAirports?.to ? buildAirportOptionFromInitial(initialAirports.to) : null,
  );
  const [fromInput, setFromInput] = useState(() =>
    initialAirports?.from ? buildAirportInputLabel(initialAirports.from) : '',
  );
  /** One-way / round-trip: filters API + list only on desktop when From dropdown is open (see isDesktopFromSplit). */
  const [fromSearchQuery, setFromSearchQuery] = useState('');
  const [toInput, setToInput] = useState(() =>
    initialAirports?.to ? buildAirportInputLabel(initialAirports.to) : '',
  );
  /** Desktop md+: To field uses in-dropdown search only (same pattern as From). */
  const [toSearchQuery, setToSearchQuery] = useState('');
  const [fromOptions, setFromOptions] = useState([]);
  const [toOptions, setToOptions] = useState([]);
  const [fromLoading, setFromLoading] = useState(false);
  const [toLoading, setToLoading] = useState(false);
  const [fromDropdownOpen, setFromDropdownOpen] = useState(false);
  const [toDropdownOpen, setToDropdownOpen] = useState(false);
  const [startDate, setStartDate] = useState(() =>
    initialSearch?.startDate ? ymdToLocalDate(initialSearch.startDate) : null,
  );
  const [endDate, setEndDate] = useState(() =>
    initialSearch?.endDate ? ymdToLocalDate(initialSearch.endDate) : null,
  );
  const [showDatePicker, setShowDatePicker] = useState(false);
  const mobileDateMonths = useDeferredMobileDateMonths(Boolean(showDatePicker && isMobile));
  /** Round-trip: which leg's calendar is open (departure = outbound fares). */
  const [activeDateField, setActiveDateField] = useState('departure');
  const [mobileDateSegmentFocused, setMobileDateSegmentFocused] = useState('departure');
  const minSelectableDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const maxSelectableDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 365);
    return d;
  }, []);
  /** Mobile fullscreen picker: draft until Save (matches Search Date UI). */
  const [mobileDateDraftStart, setMobileDateDraftStart] = useState(null);
  const [mobileDateDraftEnd, setMobileDateDraftEnd] = useState(null);
  const openDatePicker = useCallback((field = 'departure') => {
    setActiveDateField(field);
    setMobileDateSegmentFocused(field);
    setMobileDateDraftStart(startDate);
    setMobileDateDraftEnd(endDate);
    setShowDatePicker(true);
  }, [startDate, endDate]);

  const handleMobileDateSegmentFocus = useCallback((field) => {
    setMobileDateSegmentFocused(field);
    setActiveDateField(field);
  }, []);

  const handleMobileClearDeparture = useCallback(() => {
    setMobileDateDraftStart(null);
    setMobileDateSegmentFocused('departure');
    setActiveDateField('departure');
  }, []);

  const handleMobileClearReturn = useCallback(() => {
    setMobileDateDraftEnd(null);
    setMobileDateSegmentFocused('return');
    setActiveDateField('return');
  }, []);

  const handleMobileRoundTripRangeChange = useCallback((dates) => {
    if (Array.isArray(dates)) {
      const [st, en] = dates;
      setMobileDateDraftStart(st ?? null);
      setMobileDateDraftEnd(en ?? null);
      if (st && en) {
        setMobileDateSegmentFocused('return');
        setActiveDateField('return');
      } else if (st && !en) {
        // Keep departure chip stroked after first date pick (Figma 6544:99353)
        setMobileDateSegmentFocused('departure');
        setActiveDateField('departure');
      }
    } else if (dates) {
      setMobileDateDraftStart(dates);
      setMobileDateDraftEnd(null);
      setMobileDateSegmentFocused('departure');
      setActiveDateField('departure');
    }
  }, []);
  const [numAdults, setNumAdults] = useState(() => parseInitialCount(initialSearch?.adults, 1));
  const [numChildren, setNumChildren] = useState(() => parseInitialCount(initialSearch?.children, 0));
  const [numInfants, setNumInfants] = useState(() => parseInitialCount(initialSearch?.infants, 0));
  const [passengers, setPassengers] = useState(() => {
    const adults = parseInitialCount(initialSearch?.adults, 1);
    const children = parseInitialCount(initialSearch?.children, 0);
    return `${adults} Adult, ${children} Children`;
  });
  const [showPassengerDropdown, setShowPassengerDropdown] = useState(false);
  const [travelClass, setTravelClass] = useState(() => initialSearch?.class || 'ECONOMY');
  const [classTouched, setClassTouched] = useState(false);
  const [passengersTouched, setPassengersTouched] = useState(false);
  const [classDropdownOpen, setClassDropdownOpen] = useState(false);
  const [showNearbyAirports, setShowNearbyAirports] = useState(true);
  const [forceOpenMulticityField, setForceOpenMulticityField] = useState(null); // { segmentId, field: 'from' | 'to' | 'date' }
  const handleForceOpenHandled = useCallback(() => setForceOpenMulticityField(null), []);
  const [smartRecommendations, setSmartRecommendations] = useState(false);
  const [autoUpgradation, setAutoUpgradation] = useState(false);
  const [yovoSpecialReturn, setYovoSpecialReturn] = useState(false);
  const [fareType, setFareType] = useState('regular'); // 'regular' | 'student' | 'seniorCitizen'
  const [showFareTypeModal, setShowFareTypeModal] = useState(false);
  /** Mobile: tap (i) on special fare pill → white info popover sheet */
  const [specialFareInfoKey, setSpecialFareInfoKey] = useState(null);
  const openSpecialFareInfo = useCallback((key) => setSpecialFareInfoKey(key), []);
  const closeSpecialFareInfo = useCallback(() => setSpecialFareInfoKey(null), []);
  const [departureCalendarPrices, setDepartureCalendarPrices] = useState({});
  const [returnCalendarPrices, setReturnCalendarPrices] = useState({});
  const [multiCityCalendarPrices, setMultiCityCalendarPrices] = useState({});
  const calendarFareCacheRef = useRef({});
  const calendarFetchControllerRef = useRef(null);
  const fareSelectionRef = useRef({ start: null, end: null });
  const returnCalendarFetchControllerRef = useRef(null);
  const multiCityCalendarControllersRef = useRef({});

  // Mobile airport bottom sheet state
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [mobileSheetField, setMobileSheetField] = useState('from'); // 'from' | 'to'
  const mobileHostRef = useRef(null);
  const mobileSheetFieldRef = useRef('from');
  /** When set, the shared mobile airport sheet is operating on a multi-city segment (writes go to that segment, not main state). */
  const [mobileSheetMcSegmentId, setMobileSheetMcSegmentId] = useState(null);

  /** Default airport list when sheet opens or field is cleared. Uses the module-level cache so we
      avoid re-hitting the network and never re-flash "Searching airports..." once we have the list.
      Empty fetch results never overwrite existing options. */
  const loadIndiaAirportDefaults = useCallback((field) => {
    const setLoading = field === 'from' ? setFromLoading : setToLoading;
    const setOpts = field === 'from' ? setFromOptions : setToOptions;

    if (indiaDefaultsCache.options && indiaDefaultsCache.options.length > 0) {
      setOpts(indiaDefaultsCache.options);
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchIndiaAirportDefaults()
      .then((opts) => {
        if (opts && opts.length > 0) setOpts(opts);
      })
      .finally(() => setLoading(false));
  }, []);

  // Multi-city state
  const [multiCitySegments, setMultiCitySegments] = useState([
    {
      id: 1,
      fromAirport: null,
      toAirport: null,
      fromInput: '',
      toInput: '',
      fromOptions: [],
      toOptions: [],
      fromLoading: false,
      toLoading: false,
      fromDropdownOpen: false,
      toDropdownOpen: false,
      fromSearchQuery: '',
      toSearchQuery: '',
      date: null,
      showDatePicker: false
    }
  ]);

  // Refs to maintain focus on inputs
  const inputRefs = useRef({});
  const inputTimeoutRef = useRef({});

  // Refs for debouncing one-way form searches
  const lastFromSearchTermRef = useRef('');
  const lastToSearchTermRef = useRef('');
  const pendingPlaceDataRef = useRef(null); // Store place data to auto-select after search
  const mobileFromInputRef = useRef(null); // Ref for mobile sheet "From" input
  const mobileToInputRef = useRef(null);   // Ref for mobile sheet "To" input
  /** Desktop md+: wrapper roots; Popper is portaled so MUI does not always close on outside click. */
  const desktopFromFieldRootRef = useRef(null);
  const desktopToFieldRootRef = useRef(null);

  // Close main From/To Autocomplete poppers on outside pointerdown (desktop only; ignore portal list).
  useEffect(() => {
    if (isMobile || (!fromDropdownOpen && !toDropdownOpen)) return;

    const onPointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('.MuiAutocomplete-popper')) return;

      const inFrom = desktopFromFieldRootRef.current?.contains(target);
      const inTo = desktopToFieldRootRef.current?.contains(target);

      if (fromDropdownOpen && !inFrom) {
        setFromDropdownOpen(false);
        if (isDesktopFromSplit) setFromSearchQuery('');
      }
      if (toDropdownOpen && !inTo) {
        setToDropdownOpen(false);
        if (isDesktopFromSplit) setToSearchQuery('');
      }
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [isMobile, fromDropdownOpen, toDropdownOpen, isDesktopFromSplit]);

  // Function to maintain focus after state updates
  const maintainFocus = useCallback((segmentId, fieldType) => {
    const refKey = `${segmentId}-${fieldType}`;
    const inputRef = inputRefs.current[refKey];
    if (inputRef && inputRef.focus) {
      // Use multiple timing strategies to ensure focus
      setTimeout(() => {
        if (inputRef && inputRef.focus) {
          inputRef.focus();
        }
      }, 0);
      
      requestAnimationFrame(() => {
        if (inputRef && inputRef.focus) {
          inputRef.focus();
        }
      });
    }
  }, []);

  /** Close handler for the mobile airport sheet (also clears any multi-city context). */
  const closeMobileAirportSheet = useCallback(() => {
    mobileHostRef.current?.closeAirport();
    setMobileSheetOpen(false);
    setMobileSheetMcSegmentId(null);
  }, []);

  /** Mobile multicity: same bottom sheet as one-way / round-trip; mirror segment into main From/To state for shared search effects. */
  const openMcMobileAirportSheet = useCallback((segmentId, field) => {
    const seg = multiCitySegments.find((s) => s.id === segmentId);
    if (!seg) return;
    setMobileSheetMcSegmentId(segmentId);
    setMobileSheetField(field === 'to' ? 'to' : 'from');
    setFromInput(seg.fromInput || '');
    setToInput(seg.toInput || '');
    setFromAirport(seg.fromAirport ?? null);
    setToAirport(seg.toAirport ?? null);
    setMobileSheetOpen(true);
  }, [multiCitySegments]);

  /**
   * Apply an airport selection from the shared mobile sheet to a specific multi-city segment.
   * Mirrors the same validation/auto-flow as oneway/roundtrip but writes into multiCitySegments.
   */
  const applyMobileAirportToMcSegment = useCallback((segmentId, field, option) => {
    let conflict = false;
    setMultiCitySegments(prev => prev.map(s => {
      if (s.id !== segmentId) return s;
      const cityName = option.cityName || option.address?.cityName || '';
      const iataCode = option.iataCode || option.value || '';
      const cleanName = `${cityName} ${iataCode}`;
      if (field === 'from') {
        if (s.toAirport && option.value === s.toAirport.value) { conflict = 'destination'; return s; }
        return { ...s, fromAirport: option, fromInput: cleanName, fromSearchQuery: '' };
      }
      if (s.fromAirport && option.value === s.fromAirport.value) { conflict = 'origin'; return s; }
      return { ...s, toAirport: option, toInput: cleanName, toSearchQuery: '' };
    }));
    if (conflict) {
      alert(`Please select a different airport. "${option.name} (${option.iataCode})" is already selected as ${conflict}.`);
      return false;
    }
    return true;
  }, []);

  // Handle airport selection from mobile bottom sheet (mirrors Autocomplete onChange logic)
  const handleMobileAirportSelect = useCallback((option) => {
    /* Multi-city flow: route selection to the active segment instead of the main parent state. */
    if (mobileSheetMcSegmentId != null) {
      const ok = applyMobileAirportToMcSegment(mobileSheetMcSegmentId, mobileSheetField, option);
      if (!ok) return;
      const cityName = option.cityName || option.address?.cityName || '';
      const iataCode = option.iataCode || option.value || '';
      const cleanName = `${cityName} ${iataCode}`;

      if (mobileSheetField === 'from') {
        setFromAirport(option);
        setFromInput(cleanName);
        const seg = multiCitySegments.find((s) => s.id === mobileSheetMcSegmentId);
        if (!seg?.toAirport?.value) {
          setMobileSheetField('to');
          setToInput(seg?.toInput || '');
          loadIndiaAirportDefaults('to');
          setTimeout(() => mobileToInputRef.current?.focus(), 150);
        } else {
          closeMobileAirportSheet();
        }
      } else {
        setToAirport(option);
        setToInput(cleanName);
        closeMobileAirportSheet();
      }
      return;
    }

    const activeSheetField = mobileSheetFieldRef.current || mobileSheetField;
    if (activeSheetField === 'from') {
      if (toAirport && option.value === toAirport.value) {
        alert(`Please select a different airport. "${option.name} (${option.iataCode})" is already selected as destination.`);
        return;
      }
      const cityName = option.cityName || option.address?.cityName || '';
      const iataCode = option.iataCode || option.value || '';
      const stayOnTo = (tripType === 'oneway' || tripType === 'roundtrip') && !toAirport?.value;
      if (stayOnTo) {
        mobileSheetFieldRef.current = 'to';
        mobileHostRef.current?.openAirport('to');
      } else {
        mobileHostRef.current?.closeAirport();
      }
      startTransition(() => {
        setFromAirport(option);
      setFromInput(`${cityName} ${iataCode}`);
      setFromDropdownOpen(false);
        if (stayOnTo) {
        setMobileSheetField('to');
        if (!toInput || toInput.length < 2) {
          loadIndiaAirportDefaults('to');
        }
      } else {
        setMobileSheetOpen(false);
      }
      });
    } else {
      if (fromAirport && option.value === fromAirport.value) {
        alert(`Please select a different airport. "${option.name} (${option.iataCode})" is already selected as origin.`);
        return;
      }
      const cityName = option.cityName || option.address?.cityName || '';
      const iataCode = option.iataCode || option.value || '';
      const shouldOpenDate = (tripType === 'oneway' || tripType === 'roundtrip') && !startDate;
      mobileHostRef.current?.closeAirport();
      startTransition(() => {
        setToAirport(option);
      setToInput(`${cityName} ${iataCode}`);
      setToDropdownOpen(false);
      setMobileSheetOpen(false);
      });
      if (shouldOpenDate) {
        setTimeout(() => {
          mobileHostRef.current?.openDate('departure', { startDate, endDate });
        }, 100);
      }
    }
  }, [mobileSheetField, fromAirport, toAirport, tripType, toInput, startDate, loadIndiaAirportDefaults, mobileSheetMcSegmentId, applyMobileAirportToMcSegment, multiCitySegments, closeMobileAirportSheet, openDatePicker]);

  const clearMobileSheetField = useCallback((field) => {
    const isFrom = field === 'from';
    if (isFrom) {
      setFromInput('');
      setFromAirport(null);
      lastFromSearchTermRef.current = '';
      if (mobileSheetMcSegmentId != null) {
        setMultiCitySegments((prev) => prev.map((s) => {
          if (s.id !== mobileSheetMcSegmentId) return s;
          return { ...s, fromInput: '', fromAirport: null, fromSearchQuery: '' };
        }));
      }
      if (isMobile && mobileSheetOpen) loadIndiaAirportDefaults('from');
      setTimeout(() => mobileFromInputRef.current?.focus(), 0);
    } else {
      setToInput('');
      setToAirport(null);
      lastToSearchTermRef.current = '';
      if (mobileSheetMcSegmentId != null) {
        setMultiCitySegments((prev) => prev.map((s) => {
          if (s.id !== mobileSheetMcSegmentId) return s;
          return { ...s, toInput: '', toAirport: null, toSearchQuery: '' };
        }));
      }
      if (isMobile && mobileSheetOpen) loadIndiaAirportDefaults('to');
      setTimeout(() => mobileToInputRef.current?.focus(), 0);
    }
  }, [mobileSheetMcSegmentId, isMobile, mobileSheetOpen, loadIndiaAirportDefaults]);

  // Auto-focus input and load default airports when mobile sheet opens
  useEffect(() => {
    if (!isMobile || !mobileSheetOpen) return;

    const ref = mobileSheetField === 'from' ? mobileFromInputRef : mobileToInputRef;
    const currentInput = mobileSheetField === 'from' ? fromInput : toInput;

    setTimeout(() => ref.current?.focus(), 200);

    if (!currentInput || currentInput.length < 2) {
      loadIndiaAirportDefaults(mobileSheetField === 'from' ? 'from' : 'to');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileSheetOpen, mobileSheetField, isMobile]);

  /* Stable ListboxComponent identity so the in-dropdown search input is not remounted every keystroke
     (inline arrow functions would make MUI replace the listbox and drop focus). Values read from refs each render. */
  const mainFromSearchInputRef = useRef(null);
  const mainFromListboxCtxRef = useRef({
    isDesktopFromSplit: false,
    fromInput: '',
    fromSearchQuery: '',
    setFromInput: () => {},
    setFromSearchQuery: () => {},
    setFromAirport: () => {},
  });
  mainFromListboxCtxRef.current = {
    isDesktopFromSplit,
    fromInput,
    fromSearchQuery,
    setFromInput,
    setFromSearchQuery,
    setFromAirport,
  };

  const mainToListboxCtxRef = useRef({
    isDesktopFromSplit: false,
    toInput: '',
    toSearchQuery: '',
    setToInput: () => {},
    setToSearchQuery: () => {},
    setToAirport: () => {},
  });
  mainToListboxCtxRef.current = {
    isDesktopFromSplit,
    toInput,
    toSearchQuery,
    setToInput,
    setToSearchQuery,
    setToAirport,
  };

  /** Move caret into in-dropdown search after MUI focuses the (possibly read-only) combobox input. */
  const scheduleFocusMainFromSearch = useCallback(() => {
    const focus = () => mainFromSearchInputRef.current?.focus({ preventScroll: true });
    focus();
    requestAnimationFrame(() => requestAnimationFrame(focus));
    setTimeout(focus, 0);
    setTimeout(focus, 50);
    setTimeout(focus, 120);
  }, []);

  const mainToSearchInputRef = useRef(null);
  const scheduleFocusMainToSearch = useCallback(() => {
    const focus = () => mainToSearchInputRef.current?.focus({ preventScroll: true });
    focus();
    requestAnimationFrame(() => requestAnimationFrame(focus));
    setTimeout(focus, 0);
    setTimeout(focus, 50);
    setTimeout(focus, 120);
  }, []);

  const MainFromAutocompleteListbox = useMemo(
    () =>
      function MainFromAutocompleteListbox(props) {
        const { children, ...other } = props;
        const ctx = mainFromListboxCtxRef.current;
        const useSplit = ctx.isDesktopFromSplit;
        const q = useSplit ? ctx.fromSearchQuery : ctx.fromInput;
        const setQ = useSplit ? ctx.setFromSearchQuery : ctx.setFromInput;
        const setA = ctx.setFromAirport;

        useLayoutEffect(() => {
          scheduleFocusMainFromSearch();
        }, [scheduleFocusMainFromSearch]);

        /* Search lives inside the listbox <ul> so MUI's listbox ref includes it. Critical: mousedown must
           stopPropagation so the listbox root's preventDefault does not block focus moving to this input
           (see @mui/material useAutocomplete getListboxProps). */
        return (
          <ul {...other} style={{ ...other.style, padding: 0, paddingTop: 0, margin: 0 }}>
            <li
              role="presentation"
              className="flight-dropdown-from-search-host"
              style={{ listStyle: 'none', margin: 0, padding: 0, marginBottom: '12px' }}
            >
              <div className="flight-dropdown-search-bar">
                <img src={searchNormalIcon} alt="" style={{ width: '24px', height: '24px', flexShrink: 0 }} />
                <input
                  ref={mainFromSearchInputRef}
                  type="text"
                  placeholder="Search"
                  className="flight-dropdown-search-input"
                  value={q}
                  onChange={(e) => {
                    const v = e.target.value;
                    setQ(v);
                    if (!v || v.trim() === '') setA(null);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  autoComplete="off"
                  aria-label="Search airports"
                />
              </div>
            </li>
            {children}
          </ul>
        );
      },
    [scheduleFocusMainFromSearch]
  );

  const MainToAutocompleteListbox = useMemo(
    () =>
      function MainToAutocompleteListbox(props) {
        const { children, ...other } = props;
        const ctx = mainToListboxCtxRef.current;
        const useSplit = ctx.isDesktopFromSplit;
        const q = useSplit ? ctx.toSearchQuery : ctx.toInput;
        const setQ = useSplit ? ctx.setToSearchQuery : ctx.setToInput;
        const setA = ctx.setToAirport;

        useLayoutEffect(() => {
          scheduleFocusMainToSearch();
        }, [scheduleFocusMainToSearch]);

        return (
          <ul {...other} style={{ ...other.style, padding: 0, paddingTop: 0, margin: 0 }}>
            <li
              role="presentation"
              className="flight-dropdown-to-search-host"
              style={{ listStyle: 'none', margin: 0, padding: 0, marginBottom: '12px' }}
            >
              <div className="flight-dropdown-search-bar">
                <img src={searchNormalIcon} alt="" style={{ width: '24px', height: '24px', flexShrink: 0 }} />
                <input
                  ref={mainToSearchInputRef}
                  type="text"
                  placeholder="Search"
                  className="flight-dropdown-search-input"
                  value={q}
                  onChange={(e) => {
                    const v = e.target.value;
                    setQ(v);
                    if (!v || v.trim() === '') setA(null);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  autoComplete="off"
                  aria-label="Search airports"
                />
              </div>
            </li>
            {children}
          </ul>
        );
      },
    [scheduleFocusMainToSearch]
  );

  const navigate = useNavigate();
  const { updateSearchParams } = useSearchContext();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const { showPromo: showHomepageSearchPromo } = useHomepageSearchConvenienceFeePromo({
    pathname: location.pathname,
    isAuthenticated,
  });
  
  // Ref to track previous state to prevent unnecessary updates
  const prevStateRef = useRef({ tripType: 'oneway', segmentCount: 0 });

  // Memoized dependencies for useEffect to prevent infinite loops


  // Notify parent component of state changes
  // useEffect(() => {
  //   console.log("🔄 OnStateChange Prateek:",onStateChange)
  //   if (onStateChange) {
  //     const currentSegmentCount = multiCitySegments.length;
  //     const prevState = prevStateRef.current;
      
  //     // Only update if there's an actual change
  //     if (prevState.tripType !== tripType || prevState.segmentCount !== currentSegmentCount) {
  //       prevStateRef.current = { tripType, segmentCount: currentSegmentCount };
  //       onStateChange({
  //         tripType,
  //         multiCitySegments
  //       });
  //     }
  //   }
  // }, [tripType, multiCitySegments.length, onStateChange]);

  // Clear end date when switching to one way; reset special return when not roundtrip
  useEffect(() => {
    if (tripType === 'oneway') {
      setEndDate(null);
    }
    if (tripType !== 'roundtrip') {
      setYovoSpecialReturn(false);
    }
  }, [tripType]);

  useEffect(() => {
    if (yovoSpecialReturn) {
      setFareType('regular');
    }
  }, [yovoSpecialReturn]);

  // Removed unnecessary useEffect that was logging onStateChange

  // When switching to multicity, sync fromAirport into first segment
  useEffect(() => {
    if (tripType === 'multicity' && fromAirport) {
      setMultiCitySegments(prev => {
        const first = prev[0];
        if (first && !first.fromAirport) {
          const updated = [...prev];
          updated[0] = {
            ...first,
            fromAirport: fromAirport,
            fromInput: fromInput || '',
          };
          return updated;
        }
        return prev;
      });
    }
  }, [tripType, fromAirport, fromInput]);

  // Autofocus "From" field when switching to roundtrip
  useEffect(() => {
    if (tripType === 'roundtrip') {
      setTimeout(() => {
        const fromInputField = inputRefs.current['main-from'] || document.querySelector('input[placeholder*="Select Origin"], input[placeholder*="Origin"]');
        if (fromInputField) {
          fromInputField.focus();
        }
      }, 100);
    }
  }, [tripType]);

  // Request location permission automatically on page load
  useEffect(() => {
    if (disableGeolocation) return;

    // Check if geolocation is supported
    if (!navigator.geolocation) {
      console.log('Geolocation is not supported by this browser');
      return;
    }

    // Check permission status first (if Permissions API is available)
    const checkAndRequestLocation = async () => {
      try {
        // Check permission status using Permissions API (if available)
        if ('permissions' in navigator) {
          try {
            const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
            
            // If permission is already denied, don't request again
            if (permissionStatus.state === 'denied') {
              console.log('Location permission already denied by user');
              return;
            }
            
            // If permission is granted, get location and find nearest airport
            if (permissionStatus.state === 'granted') {
              try {
                const location = await getUserLocation();
                const nearestAirports = await findNearestAirportsToUser(1, 50);
                
                if (nearestAirports.success && nearestAirports.data.length > 0 && !fromAirportRef.current) {
                  const nearestAirport = nearestAirports.data[0];
                  const cityName = nearestAirport.cityName || nearestAirport.municipality || nearestAirport.address?.cityName || '';
                  const countryName = nearestAirport.countryName || nearestAirport.address?.countryName || '';
                  const airportOption = {
                    ...nearestAirport,
                    value: nearestAirport.iataCode,
                    label: `${nearestAirport.name} (${nearestAirport.iataCode}) - ${cityName}, ${countryName}`,
                    cityName: cityName,
                    countryName: countryName
                  };
                  
                  setFromAirport(airportOption);
                  const iataCode = airportOption.iataCode || '';
                  const cleanName = `${cityName} ${iataCode}`;
                  setFromInput(cleanName);
                  // Also sync into multi-city first segment
                  setMultiCitySegments(prev => {
                    const updated = [...prev];
                    updated[0] = { ...updated[0], fromAirport: airportOption, fromInput: cleanName };
                    return updated;
                  });
                  console.log('✅ Auto-filled nearest airport:', airportOption.name);
                }
              } catch (error) {
                console.log('Error getting location or nearest airport:', error.message);
              }
              return;
            }
          } catch (error) {
            // Permissions API might not be fully supported, continue with direct request
            console.log('Permissions API not fully supported, proceeding with direct request');
          }
        }

        // Request location permission (this will show browser's native prompt)
        // Only request if "From" field is empty
        if (!fromAirport) {
          getUserLocation()
            .then(async (location) => {
              console.log('✅ Location permission granted');
              // Find nearest airport and auto-fill
              try {
                const nearestAirports = await findNearestAirportsToUser(1, 50);
                
                if (nearestAirports.success && nearestAirports.data.length > 0 && !fromAirportRef.current) {
                  const nearestAirport = nearestAirports.data[0];
                  const cityName = nearestAirport.cityName || nearestAirport.municipality || nearestAirport.address?.cityName || '';
                  const countryName = nearestAirport.countryName || nearestAirport.address?.countryName || '';
                  const airportOption = {
                    ...nearestAirport,
                    value: nearestAirport.iataCode,
                    label: `${nearestAirport.name} (${nearestAirport.iataCode}) - ${cityName}, ${countryName}`,
                    cityName: cityName,
                    countryName: countryName
                  };
                  
                  setFromAirport(airportOption);
                  const iataCode = airportOption.iataCode || '';
                  const cleanName = `${cityName} ${iataCode}`;
                  setFromInput(cleanName);
                  // Also sync into multi-city first segment
                  setMultiCitySegments(prev => {
                    const updated = [...prev];
                    updated[0] = { ...updated[0], fromAirport: airportOption, fromInput: cleanName };
                    return updated;
                  });
                  console.log('✅ Auto-filled nearest airport:', airportOption.name);
                }
              } catch (error) {
                console.log('Error finding nearest airport:', error.message);
              }
            })
            .catch((error) => {
              // User denied permission or error occurred
              if (error.message.includes('denied')) {
                console.log('Location permission denied by user');
              } else {
                console.log('Location access error:', error.message);
              }
            });
        }
      } catch (error) {
        console.log('Error checking location permission:', error);
      }
    };

    // Small delay to ensure component is fully mounted
    const timer = setTimeout(() => {
      checkAndRequestLocation();
    }, 1000); // 1 second delay to avoid interrupting page load

    return () => clearTimeout(timer);
  }, [disableGeolocation]); // Only run once on mount; skip when SEO embed disables geolocation

  useEffect(() => {
    if (!showDatePicker) return undefined;
    const handleClickOutside = (event) => {
      if (
        !event.target.closest('[data-date-picker]') &&
        !event.target.closest('[data-mobile-date-sheet]')
      ) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  useEffect(() => {
    if (!isSeoHeroDesktopUx || tripType !== 'multicity') {
      setSeoMcActivePanel(null);
      return;
    }
    if (seoMcActivePanel !== 'travelers') {
      setShowPassengerDropdown(false);
    }
    if (seoMcActivePanel !== 'class') {
      setClassDropdownOpen(false);
    }
  }, [seoMcActivePanel, isSeoHeroDesktopUx, tripType]);

  useEffect(() => {
    if (!isSeoHeroDesktopUx || tripType !== 'multicity' || !seoMcActivePanel) return;

    const onPointerDown = (event) => {
      const el = event.target instanceof Element ? event.target : null;
      if (!el) return;
      if (el.closest('.MuiAutocomplete-popper')) return;
      if (el.closest('.seo-hero-mc-date-dropdown')) return;
      if (el.closest('.passenger-dropdown')) return;
      if (el.closest('.MuiPopover-root')) return;
      if (el.closest('[data-segment-id]')) return;
      if (el.closest('[data-passenger-dropdown]')) return;
      if (el.closest('.class-select-field')) return;

      setSeoMcActivePanel(null);
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [isSeoHeroDesktopUx, tripType, seoMcActivePanel]);

  // Sync mobile fullscreen draft when the sheet opens (programmatic open + field tap)
  useEffect(() => {
    if (showDatePicker && isMobile && tripType !== 'multicity') {
      setMobileDateDraftStart(startDate);
      setMobileDateDraftEnd(endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync when opening / mode, not on every startDate tick
  }, [showDatePicker, isMobile, tripType]);

  useEffect(() => {
    if (!showPassengerDropdown) return undefined;
    const handleClickOutside = (event) => {
      if (
        !event.target.closest('[data-passenger-dropdown]') &&
        !event.target.closest('[data-mobile-travellers-class-sheet]')
      ) {
        setShowPassengerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPassengerDropdown]);

  // Keep AI Travel Guide mascot from painting over the desktop travellers dropdown (Homepage.jsx)
  useEffect(() => {
    const open = showPassengerDropdown && !isMobile;
    if (open) {
      document.body.setAttribute('data-passenger-dropdown-open', 'true');
    } else {
      document.body.removeAttribute('data-passenger-dropdown-open');
    }
    return () => document.body.removeAttribute('data-passenger-dropdown-open');
  }, [showPassengerDropdown, isMobile]);

  useEffect(() => {
    if (!specialFareInfoKey) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeSpecialFareInfo();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [specialFareInfoKey, closeSpecialFareInfo]);


  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear all timeouts when component unmounts
      Object.values(inputTimeoutRef.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  /* Preload the default India airport suggestions on mount so the From / To popper opens with content
     instantly the first time the user clicks the field — eliminates the "Searching airports..." flash. */
  useEffect(() => {
    let cancelled = false;
    fetchIndiaAirportDefaults().then((opts) => {
      if (cancelled || !opts || opts.length === 0) return;
      setFromOptions((prev) => (prev.length > 0 ? prev : opts));
      setToOptions((prev) => (prev.length > 0 ? prev : opts));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const total = numAdults + numChildren + numInfants;
    if (total > 1) {
      setPassengers(`${total} Travellers`);
    } else {
      setPassengers(`${numAdults} Adult, ${numChildren} Child${numChildren === 1 ? '' : 'ren'}`);
    }
  }, [numAdults, numChildren, numInfants]);

  // Fetch airports for 'From' field
  useEffect(() => {
    if (isDesktopFromSplit && !fromDropdownOpen) {
      return;
    }

    const searchField = isDesktopFromSplit ? fromSearchQuery : fromInput;
    const iataMatch = searchField.match(/\(([A-Z]{3})\)/);
    const searchTerm = iataMatch ? iataMatch[1] : searchField;

    // Only search if the search term has actually changed
    if (searchTerm && searchTerm.length >= 2 && searchTerm !== lastFromSearchTermRef.current) {
      console.log('🔍 Searching FROM for:', searchTerm, 'based on searchField:', searchField);
      lastFromSearchTermRef.current = searchTerm;

      // Clear existing timeout to prevent multiple searches
      if (inputTimeoutRef.current['main-from-search']) {
        clearTimeout(inputTimeoutRef.current['main-from-search']);
      }

      // Debounce the search to prevent excessive API calls
      inputTimeoutRef.current['main-from-search'] = setTimeout(() => {
        setFromLoading(true);

        searchAirports(searchTerm)
          .then(async res => {
            const options = (res.data || []).map(a => {
              if (!a.iataCode) return null;

              return {
                ...a,
                value: a.iataCode,
                label: `${a.name} (${a.iataCode}) - ${a.address?.cityName || a.cityName}, ${a.address?.countryName || a.countryName}`,
                cityName: a.address?.cityName || a.cityName,
                countryName: a.address?.countryName || a.countryName
              };
            }).filter(Boolean);

            // Deduplicate airports by IATA code
            const uniqueOptions = deduplicateAirports(options);

            // Console log the JSON data for From dropdown
            console.log('🛫 FROM DROPDOWN JSON DATA:', JSON.stringify(uniqueOptions, null, 2));
            console.log('🛫 FROM DROPDOWN COUNT:', uniqueOptions.length);

            if (uniqueOptions.length === 0) {
              setFromOptions([]);
            } else {
              setFromOptions(uniqueOptions);
            }
          })
          .catch(error => {
            console.error('Error fetching from airports:', error);
            setFromOptions([]);
          })
          .finally(() => setFromLoading(false));
      }, 300); // 300ms debounce
    } else if (!searchTerm || searchTerm.length < 2) {
      // Clear options if search term is too short (do not wipe while mobile sheet shows default India list)
      if (lastFromSearchTermRef.current !== '') {
        lastFromSearchTermRef.current = '';
        if (!(isMobile && mobileSheetOpen && mobileSheetField === 'from')) {
          /* Desktop split: empty query is normal when the panel opens — do not clear or the popper is empty
             while fromInput is still filled (onOpen skips India fetch in that case). */
          if (!(isDesktopFromSplit && fromDropdownOpen)) {
            setFromOptions([]);
          }
        }
      }
    }
  }, [
    fromInput,
    fromSearchQuery,
    fromDropdownOpen,
    isDesktopFromSplit,
    showNearbyAirports,
    isMobile,
    mobileSheetOpen,
    mobileSheetField,
  ]);

  // Check for stored place data from IstanbulGuide on mount
  useEffect(() => {
    const storedPlaceData = localStorage.getItem('yovo_place_data');
    if (storedPlaceData && !toAirport) {
      try {
        const placeData = JSON.parse(storedPlaceData);
        if (placeData.city) {
          // Store place data to auto-select after search results
          pendingPlaceDataRef.current = placeData;
          
          // Set the input to trigger search
          setToInput(placeData.city);
          
          // Open dropdown to show results
          setToDropdownOpen(true);
          
          // Clear the stored data after use
          localStorage.removeItem('yovo_place_data');
        }
      } catch (error) {
        console.error('Error parsing stored place data:', error);
        localStorage.removeItem('yovo_place_data');
        pendingPlaceDataRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Fetch airports for 'To' field
  useEffect(() => {
    if (isDesktopFromSplit && !toDropdownOpen) {
      return;
    }

    const searchField = isDesktopFromSplit ? toSearchQuery : toInput;
    const iataMatch = searchField.match(/\(([A-Z]{3})\)/);
    const searchTerm = iataMatch ? iataMatch[1] : searchField;

    // Only search if the search term has actually changed
    if (searchTerm && searchTerm.length >= 2 && searchTerm !== lastToSearchTermRef.current) {
      console.log('🔍 Searching TO for:', searchTerm, 'based on searchField:', searchField);
      lastToSearchTermRef.current = searchTerm;

      // Clear existing timeout to prevent multiple searches
      if (inputTimeoutRef.current['main-to-search']) {
        clearTimeout(inputTimeoutRef.current['main-to-search']);
      }

      // Debounce the search to prevent excessive API calls
      inputTimeoutRef.current['main-to-search'] = setTimeout(() => {
        setToLoading(true);

        searchAirports(searchTerm)
          .then(async res => {
            const options = (res.data || []).map(a => {
              if (!a.iataCode) return null;

              return {
                ...a,
                value: a.iataCode,
                label: `${a.name} (${a.iataCode}) - ${a.address?.cityName || a.cityName}, ${a.address?.countryName || a.countryName}`,
                cityName: a.address?.cityName || a.cityName,
                countryName: a.address?.countryName || a.countryName
              };
            }).filter(Boolean);

            // Deduplicate airports by IATA code
            const uniqueOptions = deduplicateAirports(options);

            // Console log the JSON data for To dropdown
            console.log('🛬 TO DROPDOWN JSON DATA:', JSON.stringify(uniqueOptions, null, 2));
            console.log('🛬 TO DROPDOWN COUNT:', uniqueOptions.length);

            if (uniqueOptions.length === 0) {
              setToOptions([]);
              pendingPlaceDataRef.current = null;
            } else {
              setToOptions(uniqueOptions);
              
              // Auto-select airport if we have pending place data
              if (pendingPlaceDataRef.current && uniqueOptions.length > 0) {
                const placeData = pendingPlaceDataRef.current;
                // Try to find exact match first (by city name and country)
                let selectedAirport = uniqueOptions.find(
                  opt => opt.cityName?.toLowerCase() === placeData.city.toLowerCase() &&
                         opt.countryName?.toLowerCase() === placeData.country.toLowerCase()
                );
                
                // If no exact match, use first result
                if (!selectedAirport) {
                  selectedAirport = uniqueOptions[0];
                }
                
                // Auto-select the airport
                if (selectedAirport && (!fromAirport || selectedAirport.value !== fromAirport.value)) {
                  setToAirport(selectedAirport);
                  const cityName = selectedAirport.cityName || selectedAirport.address?.cityName || '';
                  const countryName = selectedAirport.countryName || selectedAirport.address?.countryName || '';
                  const iataCode = selectedAirport.iataCode || selectedAirport.value || '';
                  const cleanName = `${cityName} ${iataCode}`;
                  setToInput(cleanName);
                  setToDropdownOpen(false);
                  
                  // Auto-open date picker for one-way and round-trip trips
                  if ((tripType === 'oneway' || tripType === 'roundtrip') && !startDate) {
                    setTimeout(() => openDatePicker('departure'), 100);
                  }
                }
                
                // Clear pending place data
                pendingPlaceDataRef.current = null;
              }
            }
          })
          .catch(error => {
            console.error('Error fetching to airports:', error);
            setToOptions([]);
            pendingPlaceDataRef.current = null;
          })
          .finally(() => setToLoading(false));
      }, 300); // 300ms debounce
    } else if (!searchTerm || searchTerm.length < 2) {
      if (lastToSearchTermRef.current !== '') {
        lastToSearchTermRef.current = '';
        if (!(isMobile && mobileSheetOpen && mobileSheetField === 'to')) {
          if (!(isDesktopFromSplit && toDropdownOpen)) {
            setToOptions([]);
          }
        }
      }
    }
  }, [
    toInput,
    toSearchQuery,
    toDropdownOpen,
    isDesktopFromSplit,
    showNearbyAirports,
    fromAirport,
    tripType,
    startDate,
    isMobile,
    mobileSheetOpen,
    mobileSheetField,
  ]);

  // Outbound calendar fares (FROM → TO) for one-way and round-trip departure
  useEffect(() => {
    if (!enableCalendarFares) {
      setDepartureCalendarPrices({});
      return;
    }
    if (tripType === 'multicity') {
      setDepartureCalendarPrices({});
      return;
    }
    if (!fromAirport?.value || !toAirport?.value) {
      setDepartureCalendarPrices({});
      return;
    }

    if (calendarFetchControllerRef.current) {
      calendarFetchControllerRef.current.abort();
    }
    const controller = new AbortController();
    calendarFetchControllerRef.current = controller;

    const fetchCalendarFares = async () => {
      const origin = fromAirport.value;
      const destination = toAirport.value;
      const today = new Date();
      const months = [];
      for (let i = 0; i < 3; i++) {
        const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
        months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      }

      const routeKey = `${origin}-${destination}`;
      const allPrices = {};

      try {
        const promises = months.map(async (month) => {
          const cacheKey = `${routeKey}:${month}`;
          const cached = calendarFareCacheRef.current[cacheKey];
          if (cached && Date.now() - cached.ts < 30 * 60 * 1000) {
            return cached.data;
          }
          const response = await apiService.getCalendarFare(origin, destination, month);
          const data = response.data?.data || { fares: {} };
          calendarFareCacheRef.current[cacheKey] = { data, ts: Date.now() };
          return data;
        });

        const results = await Promise.allSettled(promises);
        if (controller.signal.aborted) return;

        for (const result of results) {
          if (result.status === 'fulfilled' && result.value?.fares) {
            Object.entries(result.value.fares).forEach(([dateKey, info]) => {
              allPrices[dateKey] = info.fare;
            });
          }
        }

        setDepartureCalendarPrices(allPrices);
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Error fetching departure calendar fares:', error);
        }
      }
    };

    fetchCalendarFares();

    return () => {
      controller.abort();
    };
  }, [enableCalendarFares, tripType, fromAirport?.value, toAirport?.value]);

  // Inbound calendar fares (TO → FROM) for round-trip return only
  useEffect(() => {
    if (!enableCalendarFares) {
      setReturnCalendarPrices({});
      return;
    }
    if (tripType !== 'roundtrip') {
      setReturnCalendarPrices({});
      return;
    }
    if (!fromAirport?.value || !toAirport?.value) {
      setReturnCalendarPrices({});
      return;
    }

    if (returnCalendarFetchControllerRef.current) {
      returnCalendarFetchControllerRef.current.abort();
    }
    const controller = new AbortController();
    returnCalendarFetchControllerRef.current = controller;

    const fetchReturnFares = async () => {
      const origin = toAirport.value;
      const destination = fromAirport.value;
      const today = new Date();
      const months = [];
      for (let i = 0; i < 3; i++) {
        const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
        months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      }

      const routeKey = `${origin}-${destination}`;
      const allPrices = {};

      try {
        const promises = months.map(async (month) => {
          const cacheKey = `${routeKey}:${month}`;
          const cached = calendarFareCacheRef.current[cacheKey];
          if (cached && Date.now() - cached.ts < 30 * 60 * 1000) {
            return cached.data;
          }
          const response = await apiService.getCalendarFare(origin, destination, month);
          const data = response.data?.data || { fares: {} };
          calendarFareCacheRef.current[cacheKey] = { data, ts: Date.now() };
          return data;
        });

        const results = await Promise.allSettled(promises);
        if (controller.signal.aborted) return;

        for (const result of results) {
          if (result.status === 'fulfilled' && result.value?.fares) {
            Object.entries(result.value.fares).forEach(([dateKey, info]) => {
              allPrices[dateKey] = info.fare;
            });
          }
        }

        setReturnCalendarPrices(allPrices);
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Error fetching return calendar fares:', error);
        }
      }
    };

    fetchReturnFares();

    return () => {
      controller.abort();
    };
  }, [enableCalendarFares, tripType, fromAirport?.value, toAirport?.value]);

  // Stable key tracking which segments have both airports selected
  const multiCityRouteKey = useMemo(() =>
    multiCitySegments.map(s => `${s.fromAirport?.value || ''}-${s.toAirport?.value || ''}`).join(','),
    [multiCitySegments]
  );

  // Fetch calendar fares per multi-city segment
  useEffect(() => {
    if (!enableCalendarFares) {
      setMultiCityCalendarPrices({});
      return;
    }
    if (tripType !== 'multicity') return;

    Object.values(multiCityCalendarControllersRef.current).forEach(c => c.abort());
    multiCityCalendarControllersRef.current = {};

    const fetchSegmentFares = async (segIndex, origin, destination) => {
      const controller = new AbortController();
      multiCityCalendarControllersRef.current[segIndex] = controller;

      const today = new Date();
      const months = [];
      for (let i = 0; i < 3; i++) {
        const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
        months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      }

      const routeKey = `${origin}-${destination}`;
      const segPrices = {};

      try {
        const promises = months.map(async (month) => {
          const cacheKey = `${routeKey}:${month}`;
          const cached = calendarFareCacheRef.current[cacheKey];
          if (cached && Date.now() - cached.ts < 30 * 60 * 1000) {
            return cached.data;
          }
          const response = await apiService.getCalendarFare(origin, destination, month);
          const data = response.data?.data || { fares: {} };
          calendarFareCacheRef.current[cacheKey] = { data, ts: Date.now() };
          return data;
        });

        const results = await Promise.allSettled(promises);
        if (controller.signal.aborted) return;

        for (const result of results) {
          if (result.status === 'fulfilled' && result.value?.fares) {
            Object.entries(result.value.fares).forEach(([dateKey, info]) => {
              segPrices[dateKey] = info.fare;
            });
          }
        }

        if (!controller.signal.aborted) {
          setMultiCityCalendarPrices(prev => ({ ...prev, [segIndex]: segPrices }));
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error(`Error fetching calendar fares for segment ${segIndex}:`, error);
        }
      }
    };

    const newPrices = {};
    multiCitySegments.forEach((seg, idx) => {
      const origin = seg.fromAirport?.value;
      const dest = seg.toAirport?.value;
      if (origin && dest) {
        fetchSegmentFares(idx, origin, dest);
      } else {
        newPrices[idx] = {};
      }
    });
    setMultiCityCalendarPrices(prev => ({ ...prev, ...newPrices }));

    return () => {
      Object.values(multiCityCalendarControllersRef.current).forEach(c => c.abort());
    };
  }, [enableCalendarFares, tripType, multiCityRouteKey]);

  // Fetch airports for multi-city segments - TO fields
  // useEffect(() => {
  //   multiCitySegments.forEach(segment => {
  //     if (segment.toInput && segment.toInput.length >= 2) {
  //       updateMultiCitySegment(segment.id, 'toLoading', true);
        
  //       const iataMatch = segment.toInput.match(/\(([A-Z]{3})\)/);
  //       const searchTerm = iataMatch ? iataMatch[1] : segment.toInput;
        
  //       searchAirports(searchTerm)
  //         .then(async res => {
  //           const options = (res.data || []).map(a => {
  //             if (!a.iataCode) return null;
              
  //             return {
  //               ...a,
  //               value: a.iataCode,
  //               label: `${a.name} (${a.iataCode}) - ${a.address?.cityName || a.cityName}, ${a.address?.countryName || a.countryName}`,
  //               cityName: a.address?.cityName || a.cityName,
  //               countryName: a.address?.countryName || a.countryName
  //             };
  //           }).filter(Boolean);

  //           // Deduplicate airports by IATA code
  //           const uniqueOptions = deduplicateAirports(options);

  //           if (uniqueOptions.length === 0) {
  //             updateMultiCitySegment(segment.id, 'toOptions', []);
  //           } else {
  //             updateMultiCitySegment(segment.id, 'toOptions', uniqueOptions);
  //           }
  //         })
  //         .catch(error => {
  //           updateMultiCitySegment(segment.id, 'toOptions', []);
  //         })
  //         .finally(() => updateMultiCitySegment(segment.id, 'toLoading', false));
  //     } else {
  //       updateMultiCitySegment(segment.id, 'toOptions', []);
  //     }
  //   });
  // }, [toInputDeps, showNearbyAirports]);

  const totalPassengers = numAdults + numChildren + numInfants;

  const firstMulticitySegmentDate = multiCitySegments[0]?.date;
  const travellersSheetFooterDate = useMemo(() => {
    if (tripType === 'multicity') {
      return firstMulticitySegmentDate ? formatShortTripDate(firstMulticitySegmentDate) : 'Select date';
    }
    if (tripType === 'roundtrip' && startDate && endDate) {
      return `${formatShortTripDate(startDate)} – ${formatShortTripDate(endDate)}`;
    }
    if (startDate) return formatShortTripDate(startDate);
    return 'Select date';
  }, [tripType, startDate, endDate, firstMulticitySegmentDate]);

  const pickMobileAdults = useCallback((n) => {
    if (n < 1 || n > 9) return;
    const nextInfants = Math.min(numInfants, n);
    if (n + numChildren + nextInfants > 9) return;
    setNumAdults(n);
    setNumInfants(nextInfants);
    setPassengersTouched(true);
  }, [numChildren, numInfants]);

  const pickMobileChildren = useCallback((c) => {
    if (fareType === 'student' || fareType === 'seniorCitizen') return;
    if (c < 0 || c > 8) return;
    if (numAdults + c + numInfants > 9) return;
    setNumChildren(c);
    setPassengersTouched(true);
  }, [numAdults, numInfants, fareType]);

  const pickMobileInfants = useCallback((i) => {
    if (fareType === 'student' || fareType === 'seniorCitizen') return;
    if (i < 0 || i > 8) return;
    if (i > numAdults) return;
    if (numAdults + numChildren + i > 9) return;
    setNumInfants(i);
    setPassengersTouched(true);
  }, [numAdults, numChildren, fareType]);

  const getPassengerCounts = () => {
    return { adults: numAdults, children: numChildren, infants: numInfants };
  };

  // Multi-city helper functions
  const addMultiCitySegment = () => {
    if (multiCitySegments.length < 5) {
      const newSegment = {
        id: Date.now(),
        fromAirport: null,
        toAirport: null,
        fromInput: '',
        toInput: '',
        fromOptions: [],
        toOptions: [],
        fromLoading: false,
        toLoading: false,
        fromDropdownOpen: false,
        toDropdownOpen: false,
        fromSearchQuery: '',
        toSearchQuery: '',
        date: null,
        showDatePicker: false
      };
      setMultiCitySegments([...multiCitySegments, newSegment]);
    }
  };

  const removeMultiCitySegment = (segmentId) => {
    if (multiCitySegments.length > 1) {
      setMultiCitySegments(multiCitySegments.filter(segment => segment.id !== segmentId));
    }
  };

  const updateMultiCitySegment = useCallback((segmentId, field, value) => {
    console.log('updateMultiCitySegment called:', { segmentId, field, value });
    setMultiCitySegments(segments => 
      segments.map(segment => 
        segment.id === segmentId ? { ...segment, [field]: value } : segment
      )
    );
  }, []);

  // Batch update function for multiple field changes
  const updateMultiCitySegmentBatch = useCallback((segmentId, updates) => {
    console.log('updateMultiCitySegmentBatch called:', { segmentId, updates });
    setMultiCitySegments(segments => 
      segments.map(segment => 
        segment.id === segmentId ? { ...segment, ...updates } : segment
      )
    );
  }, []);

  const formatCalendarPrice = useCallback((price) => {
    if (price >= 100000) return `₹${(price / 100000).toFixed(1)}L`;
    if (price >= 1000) return `₹${(price / 1000).toFixed(1)}k`;
    return `₹${price}`;
  }, []);

  const buildPriceThresholds = useCallback((pricesMap) => {
    const prices = Object.values(pricesMap).filter((p) => typeof p === 'number' && p > 0);
    if (prices.length < 3) return null;
    const sorted = [...prices].sort((a, b) => a - b);
    return {
      p25: sorted[Math.floor(sorted.length * 0.25)],
      p75: sorted[Math.floor(sorted.length * 0.75)],
    };
  }, []);

  const departurePriceThresholds = useMemo(
    () => buildPriceThresholds(departureCalendarPrices),
    [buildPriceThresholds, departureCalendarPrices]
  );
  const returnPriceThresholds = useMemo(
    () => buildPriceThresholds(returnCalendarPrices),
    [buildPriceThresholds, returnCalendarPrices]
  );

  const getPriceColor = useCallback((price, thresholds) => {
    if (!thresholds) return '#2e7d32';
    if (price <= thresholds.p25) return '#2e7d32';
    if (price <= thresholds.p75) return '#e67e00';
    return '#d32f2f';
  }, []);

  const makeRenderDayContents = useCallback((pricesMap, thresholds) => (day, date) => {
    if (!date) return <span>{day}</span>;
    const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const price = pricesMap[dateKey];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1, minHeight: '36px', justifyContent: 'center' }}>
        <span>{day}</span>
        {price != null && (
          <span style={{ fontSize: '9px', color: getPriceColor(price, thresholds), fontWeight: 600, marginTop: '1px', whiteSpace: 'nowrap' }}>
            {formatCalendarPrice(price)}
          </span>
        )}
      </div>
    );
  }, [formatCalendarPrice, getPriceColor]);

  const renderDepartureDayContents = useMemo(
    () => makeRenderDayContents(departureCalendarPrices, departurePriceThresholds),
    [makeRenderDayContents, departureCalendarPrices, departurePriceThresholds]
  );
  const renderReturnDayContents = useMemo(
    () => makeRenderDayContents(returnCalendarPrices, returnPriceThresholds),
    [makeRenderDayContents, returnCalendarPrices, returnPriceThresholds]
  );
  const renderDayContents = renderDepartureDayContents;


  // Test function for multi-city API
  const testMulticityAPI = async () => {
    console.log('🧪 Testing Multi-city API...');
    
    // Create test segments
    const testSegments = [
      {
        from: 'DEL', // Delhi
        to: 'BOM',   // Mumbai
        date: '2024-02-15'
      },
      {
        from: 'BOM', // Mumbai
        to: 'BLR',   // Bangalore
        date: '2024-02-20'
      }
    ];
    
    const testParams = {
      adults: 1,
      children: 0,
      travelClass: 'ECONOMY',
      currencyCode: getSavedCurrency(),
      max: 250
    };
    
    try {
      console.log('🔍 Test segments:', testSegments);
      console.log('🔍 Test params:', testParams);
      
      console.log('Multi-city test disabled — Amadeus removed, using TBO');
      alert('Multi-city search now uses TBO backend. Test via the search flow.');
      
    } catch (error) {
      console.error('❌ Multi-city API test failed:', error);
      alert(`Multi-city API test failed: ${error.message}`);
    }
  };

  const isSpecialFare = fareType === 'student' || fareType === 'seniorCitizen';
  const hasChildrenOrInfants = numChildren > 0 || numInfants > 0;

  const handleFareTypeCheckbox = (type) => {
    if (fareType === type) {
      setFareType('regular');
    } else {
      // Armed Forces allows children & infants; Student/Senior Citizen do not
      if (type !== 'armedForces' && hasChildrenOrInfants) return;
      setFareType(type);
      if (type !== 'armedForces') {
        setNumChildren(0);
        setNumInfants(0);
      }
      if (yovoSpecialReturn) {
        setYovoSpecialReturn(false);
      }
    }
  };

  // Removed fare types: migrate any stale selection
  useEffect(() => {
    setFareType((ft) => (ft === 'doctorNurse' ? 'regular' : ft));
  }, []);

  // Search handler
  const handleSearch = async (skipFareModal = false) => {
    const { adults, children, infants } = getPassengerCounts();
    
    const formatDate = (date) => {
      if (!date) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    // Validation and auto-open dropdowns/pickers
    if (tripType === 'multicity') {
      // Check multicity segments
      for (let i = 0; i < multiCitySegments.length; i++) {
        const segment = multiCitySegments[i];
        if (!segment.fromAirport?.value) {
          // Set flag to force open this field
          setForceOpenMulticityField({ segmentId: segment.id, field: 'from' });
          // Load options first
          if (!segment.fromInput || segment.fromInput.length < 2) {
            updateMultiCitySegment(segment.id, 'fromLoading', true);
            searchAirports('India')
              .then(async res => {
                const options = (res.data || []).map(a => {
                  if (!a.iataCode) return null;
                  return {
                    ...a,
                    value: a.iataCode,
                    label: `${a.name} (${a.iataCode}) - ${a.address?.cityName || a.cityName}, ${a.address?.countryName || a.countryName}`,
                    cityName: a.address?.cityName || a.cityName,
                    countryName: a.address?.countryName || a.countryName
                  };
                }).filter(Boolean);
                const uniqueOptions = deduplicateAirports(options);
                updateMultiCitySegment(segment.id, 'fromOptions', uniqueOptions.slice(0, 20));
                updateMultiCitySegment(segment.id, 'fromLoading', false);
              })
              .catch(error => {
                console.error('Error fetching popular airports:', error);
                updateMultiCitySegment(segment.id, 'fromOptions', []);
                updateMultiCitySegment(segment.id, 'fromLoading', false);
              });
          }
          // Scroll to the segment
          setTimeout(() => {
            const segmentElement = document.querySelector(`[data-segment-id="${segment.id}"]`);
            if (segmentElement) {
              segmentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          return;
        }
        if (!segment.toAirport?.value) {
          // Set flag to force open this field
          setForceOpenMulticityField({ segmentId: segment.id, field: 'to' });
          // Load options first
          if (!segment.toInput || segment.toInput.length < 2) {
            updateMultiCitySegment(segment.id, 'toLoading', true);
            searchAirports('India')
              .then(async res => {
                const options = (res.data || []).map(a => {
                  if (!a.iataCode) return null;
                  return {
                    ...a,
                    value: a.iataCode,
                    label: `${a.name} (${a.iataCode}) - ${a.address?.cityName || a.cityName}, ${a.address?.countryName || a.countryName}`,
                    cityName: a.address?.cityName || a.cityName,
                    countryName: a.address?.countryName || a.countryName
                  };
                }).filter(Boolean);
                const uniqueOptions = deduplicateAirports(options);
                updateMultiCitySegment(segment.id, 'toOptions', uniqueOptions.slice(0, 20));
                updateMultiCitySegment(segment.id, 'toLoading', false);
              })
              .catch(error => {
                console.error('Error fetching popular airports:', error);
                updateMultiCitySegment(segment.id, 'toOptions', []);
                updateMultiCitySegment(segment.id, 'toLoading', false);
              });
          }
          // Scroll to the segment
          setTimeout(() => {
            const segmentElement = document.querySelector(`[data-segment-id="${segment.id}"]`);
            if (segmentElement) {
              segmentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          return;
        }
        if (!segment.date) {
          // Set flag to force open date picker
          setForceOpenMulticityField({ segmentId: segment.id, field: 'date' });
          // Open date picker for this segment
          updateMultiCitySegment(segment.id, 'showDatePicker', true);
          // Scroll to the segment
          setTimeout(() => {
            const segmentElement = document.querySelector(`[data-segment-id="${segment.id}"]`);
            if (segmentElement) {
              segmentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          return;
        }
      }
    } else {
      // One-way and Round-trip validation
      if (!fromAirport?.value) {
        setFromDropdownOpen(true);
        // Trigger API call to show suggestions
        if (!fromInput || fromInput.length < 2) {
          setFromLoading(true);
          searchAirports('India')
            .then(async res => {
              const options = (res.data || []).map(a => {
                if (!a.iataCode) return null;
                return {
                  ...a,
                  value: a.iataCode,
                  label: `${a.name} (${a.iataCode}) - ${a.address?.cityName || a.cityName}, ${a.address?.countryName || a.countryName}`,
                  cityName: a.address?.cityName || a.cityName,
                  countryName: a.address?.countryName || a.countryName
                };
              }).filter(Boolean);
              const uniqueOptions = deduplicateAirports(options);
              setFromOptions(uniqueOptions.slice(0, 20));
            })
            .catch(error => {
              console.error('Error fetching popular airports:', error);
              setFromOptions([]);
            })
            .finally(() => setFromLoading(false));
        }
        // Focus the from input field
        setTimeout(() => {
          const fromInputField = inputRefs.current['main-from'] || document.querySelector('input[placeholder*="Select Origin"], input[placeholder*="Origin"]');
          if (fromInputField) {
            fromInputField.focus();
          }
        }, 100);
        return;
      }
      if (!toAirport?.value) {
        setToDropdownOpen(true);
        // Trigger API call to show suggestions
        if (!toInput || toInput.length < 2) {
          setToLoading(true);
          searchAirports('India')
            .then(async res => {
              const options = (res.data || []).map(a => {
                if (!a.iataCode) return null;
                return {
                  ...a,
                  value: a.iataCode,
                  label: `${a.name} (${a.iataCode}) - ${a.address?.cityName || a.cityName}, ${a.address?.countryName || a.countryName}`,
                  cityName: a.address?.cityName || a.cityName,
                  countryName: a.address?.countryName || a.countryName
                };
              }).filter(Boolean);
              const uniqueOptions = deduplicateAirports(options);
              setToOptions(uniqueOptions.slice(0, 20));
            })
            .catch(error => {
              console.error('Error fetching popular airports:', error);
              setToOptions([]);
            })
            .finally(() => setToLoading(false));
        }
        // Focus the to input field
        setTimeout(() => {
          const toInputField = inputRefs.current['main-to'] || document.querySelector('input[placeholder*="Select Destination"], input[placeholder*="Destination"]');
          if (toInputField) {
            toInputField.focus();
          }
        }, 100);
        return;
      }
      if (!startDate) {
        openDatePicker('departure');
        return;
      }
      if (tripType === 'roundtrip' && !endDate) {
        openDatePicker('return');
        return;
      }
    }

    if (!skipFareModal && (fareType === 'student' || fareType === 'armedForces' || fareType === 'seniorCitizen')) {
      setShowFareTypeModal(true);
      return;
    }
    
    if (tripType === 'multicity') {
      // Multi-city search - use main parent segments
      
      const segments = multiCitySegments.map(segment => ({
        from: segment.fromAirport?.value || '',
        to: segment.toAirport?.value || '',
        date: formatDate(segment.date)
      }));
      
      // Validate multicity segments
      const validSegments = segments.filter(segment => 
        segment.from && segment.to && segment.date
      );
      
      console.log('✅ Valid segments for API:', validSegments);
      
      if (validSegments.length === 0) {
        alert('Please fill in all multicity segments with valid airports and dates.');
        return;
      }
      
      // Update global search context (without API results)
      updateSearchParams({
        segments: validSegments,
        adults: adults,
        children: children,
        infants: infants,
        travelClass: travelClass,
        tripType: tripType
      });
      
      // Create URL parameters for multi-city - separate each segment field
      const params = new URLSearchParams();
      
      // Add common parameters
      params.append('tripType', tripType);
      params.append('adults', adults.toString());
      params.append('children', children.toString());
      params.append('infants', infants.toString());
      params.append('class', travelClass);
      params.append('segmentCount', validSegments.length.toString());
      
      // Add each segment's parameters separately
      validSegments.forEach((segment, index) => {
        params.append(`segment${index + 1}_from`, segment.from);
        params.append(`segment${index + 1}_to`, segment.to);
        params.append(`segment${index + 1}_date`, segment.date);
      });
      
      console.log('🔗 Multi-city URL parameters:', params.toString());
      navigate(`/flight?${params.toString()}`);
    } else {
      // One-way and Round-trip search
      const formattedStartDate = formatDate(startDate);
      const formattedEndDate = tripType === 'oneway' ? '' : formatDate(endDate);
      
      // Update global search context
      updateSearchParams({
        from: fromAirport?.value || '',
        to: toAirport?.value || '',
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        adults: adults,
        children: children,
        infants: infants,
        travelClass: travelClass,
        tripType: tripType,
      });
      
      const params = new URLSearchParams({
        from: fromAirport?.value || '',
        to: toAirport?.value || '',
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        adults: adults.toString(),
        children: children.toString(),
        infants: infants.toString(),
        class: travelClass,
        tripType: tripType,
      });
      if (yovoSpecialReturn) {
        params.set('yovoSpecialReturn', 'true');
      }
      if (fareType !== 'regular') {
        params.set('fareType', fareType);
      }
      navigate(`/flight?${params.toString()}`);
    }
  };

  const closeMobileMainDatePicker = useCallback(() => {
    setShowDatePicker(false);
  }, []);

  const saveMobileMainDatePicker = useCallback(() => {
    if (!mobileDateDraftStart) return;
    if (tripType === 'roundtrip' && !mobileDateDraftEnd) {
      setMobileDateSegmentFocused('return');
      setActiveDateField('return');
      return;
    }
    setStartDate(mobileDateDraftStart);
    setEndDate(tripType === 'roundtrip' ? mobileDateDraftEnd : null);
    setShowDatePicker(false);
  }, [mobileDateDraftStart, mobileDateDraftEnd, tripType]);

  const renderMobileFullscreenMainCalendars = (draft) => {
    const selectedStart = draft ? draft.start : mobileDateDraftStart;
    const selectedEnd = draft ? draft.end : mobileDateDraftEnd;
    const months = draft ? draft.months : mobileDateMonths;
    if (!months || months < 1) {
      fareSelectionRef.current = { start: selectedStart || null, end: selectedEnd || null };
      return null;
    }
    return (
      <Box className="mobile-date-fullscreen-datepicker-wrap">
        <FareDateCalendar
          monthCount={months}
          minDate={minSelectableDate}
          maxDate={maxSelectableDate}
          tripType={tripType === 'roundtrip' ? 'roundtrip' : 'oneway'}
          departurePrices={departureCalendarPrices}
          returnPrices={returnCalendarPrices}
          initialStart={selectedStart}
          initialEnd={selectedEnd}
          externalStart={selectedStart}
          externalEnd={selectedEnd}
          onSelectionChange={draft ? draft.setRange : handleMobileRoundTripRangeChange}
          publishedSelectionRef={fareSelectionRef}
        />
      </Box>
    );
  };

  /** One-way / round-trip inline calendars — reused for desktop popper and mobile bottom sheet. */
  const renderMainSegmentDateCalendars = () =>
    tripType === 'oneway' ? (
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            mt: 0,
            '& .react-datepicker': {
              display: 'flex !important',
              flexDirection: { xs: 'column !important', md: 'row !important' },
              gap: { xs: '8px !important', md: '12px !important' },
            },
          }}
        >
          <DatePicker
            selected={startDate}
            onChange={(date) => {
              setStartDate(date);
              if (date) {
                setShowDatePicker(false);
              }
            }}
            dateFormat="MMM dd, yyyy"
            minDate={new Date()}
            maxDate={maxSelectableDate}
            inline
            showPopperArrow={false}
            calendarClassName="custom-datepicker-single-month"
            monthsShown={isMobile ? 1 : 2}
            showPreviousMonths={false}
            openToDate={startDate || new Date()}
            renderDayContents={renderDayContents}
            selectsRange={false}
            shouldCloseOnSelect
            formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 3)}
          />
        </Box>
      </Box>
    ) : (
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            mt: isMobile ? 0 : 1,
            '& .react-datepicker': {
              display: 'flex !important',
              flexDirection: { xs: 'column !important', md: 'row !important' },
              gap: { xs: '8px !important', md: '12px !important' },
            },
          }}
        >
          <DatePicker
            key={`roundtrip-${activeDateField}-${activeDateField === 'return' ? endDate?.getTime() : startDate?.getTime() || 'null'}`}
            selected={activeDateField === 'return' ? endDate : startDate}
            onChange={(date) => {
              if (activeDateField === 'return') {
                setEndDate(date);
                if (date) setShowDatePicker(false);
              } else {
                setStartDate(date);
                if (date && endDate && endDate < date) setEndDate(null);
                if (date) setShowDatePicker(false);
              }
            }}
            dateFormat="MMM dd, yyyy"
            minDate={activeDateField === 'return' ? (startDate || new Date()) : new Date()}
            maxDate={maxSelectableDate}
            inline
            showPopperArrow={false}
            calendarClassName="custom-datepicker-single-month"
            shouldCloseOnSelect
            monthsShown={isMobile ? 1 : 2}
            showPreviousMonths={false}
            openToDate={(activeDateField === 'return' ? endDate : startDate) || startDate || new Date()}
            formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 3)}
            renderDayContents={activeDateField === 'return' ? renderReturnDayContents : renderDepartureDayContents}
            selectsRange={false}
          />
        </Box>
      </Box>
    );

  return (
    <Box sx={{
      position: 'relative',
      zIndex: showPassengerDropdown && !isMobile ? 1400 : { xs: 'auto', sm: 'auto', md: 'auto' },
      mt: isEmbedded
        ? 0
        : tripType === 'multicity'
          ? { xs: '0px', sm: '0px', md: '-120px' }
          : { xs: '0px', sm: '0px', md: '-90px' },
      width: isEmbedded
        ? '100%'
        : {
            xs: '100%',
            sm: '100%',
            md: 'calc(100% - 80px)',
          },
      maxWidth: isEmbedded
        ? '100%'
        : {
            xs: '100%',
            sm: '100%',
            md: '1160px',
            lg: '1360px',
            xl: '1560px',
          },
      mx: 'auto',
      px: isEmbedded ? 0 : { xs: '0px', sm: '0px', md: '0px' },
    }}>
      <Card sx={{
        borderRadius: isEmbedded
          ? 2
          : { xs: L.formCardRadius, sm: L.formCardRadius, md: '32px' },
        border: '1px solid var(--yovo-grey-300)',
        background: isEmbedded
          ? '#fff'
          : { xs: '#E8E9ED', sm: '#E8E9ED', md: 'var(--yovo-base-white)' },
        boxShadow: isEmbedded ? 'none' : 'var(--yovo-shadow-card)',
        overflow: 'visible',
        position: 'relative',
        zIndex: isEmbedded ? 1 : { xs: 2, sm: 2, md: 1 },
        width: '100%',
        boxSizing: 'border-box',
      }}>
        <CardContent sx={{
          pt: isEmbedded
            ? { xs: 2, md: 2.5 }
            : { xs: L.formCardPaddingY, sm: L.formCardPaddingY, md: '24px' },
          pb: isEmbedded
            ? { xs: 2, md: 2.5 }
            : { xs: L.formCardPaddingY, sm: L.formCardPaddingY, md: '24px' },
          pl: isEmbedded
            ? { xs: 2, md: 2.5 }
            : { xs: L.formCardPaddingX, sm: L.formCardPaddingX, md: '40px' },
          pr: isEmbedded
            ? { xs: 2, md: 2.5 }
            : { xs: L.formCardPaddingX, sm: L.formCardPaddingX, md: '40px' },
          display: { xs: 'flex', sm: 'flex', md: 'block' },
          flexDirection: { xs: 'column', sm: 'column', md: 'initial' },
          gap: isEmbedded
            ? { xs: 1.5, md: 1.5 }
            : { xs: L.formCardSectionGap, sm: L.formCardSectionGap, md: 0 },
        }}>
          {/* Trip Type Selection — first row inside floating grey card (Figma mobile) */}
          <Box sx={{ width: '100%', mb: 0.5, position: 'relative', zIndex: 1 }}>
            {!hideTripTypeSelector && (
            <Box sx={{
              display: 'flex',
              gap: { xs: '24px', sm: '24px', md: '0px' },
              mb: { xs: '6px', sm: '6px', md: 0.5 },
              alignItems: 'center',
              justifyContent: { xs: 'center', sm: 'center', md: 'flex-start' },
              flexWrap: 'wrap',
              pb: { xs: 0, sm: 0, md: 0.3 },
              px: { xs: 0, sm: 0, md: 0 },
              width: '100%',
            }}>
              <FormControlLabel
                control={
                  <IconButton
                    {...createGtmAttributes(GTM_CLICK_IDS.FLIGHT_SEARCH_TRIP_ONEWAY)}
                    onClick={() => changeTripType('oneway')}
                    sx={{
                      p: 0.2,
                      width: '20px',
                      height: '20px',
                      '&:hover': {
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={tripType === 'oneway' ? radioButtonFilled : radioButtonEmpty}
                      alt="radio button"
                      sx={{
                        width: '20px',
                        height: '20px',
                        display: 'block'
                      }}
                    />
                  </IconButton>
                }
                label={<Typography sx={{ 
                  fontFamily: 'var(--yovo-font-primary)',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '16px',
                  letterSpacing: '0.37px',
                  color: 'var(--yovo-grey-800)'
                }}>One way</Typography>}
                sx={{ 
                  ml: 0, 
                  gap: '8px',
                  marginTop: { xs: 0, md: '1.63px' },
                  marginLeft: { xs: 0, md: '6.62px' },
                  marginRight: { xs: 0, md: '48px' },
                  marginBottom: { xs: 0, md: '9.75px' },
                }}
              />
              <FormControlLabel
                control={
                  <IconButton
                    {...createGtmAttributes(GTM_CLICK_IDS.FLIGHT_SEARCH_TRIP_ROUNDTRIP)}
                    onClick={() => changeTripType('roundtrip')}
                    sx={{
                      p: 0.2,
                      width: '20px',
                      height: '20px',
                      '&:hover': {
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={tripType === 'roundtrip' ? radioButtonFilled : radioButtonEmpty}
                      alt="radio button"
                      sx={{
                        width: '20px',
                        height: '20px',
                        display: 'block'
                      }}
                    />
                  </IconButton>
                }
                label={<Typography sx={{ 
                  fontFamily: 'var(--yovo-font-primary)',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '16px',
                  letterSpacing: '0.37px',
                  color: 'var(--yovo-grey-800)'
                }}>Round Trip</Typography>}
                sx={{ 
                  gap: '8px',
                  marginTop: { xs: 0, md: '1.63px' },
                  marginRight: { xs: 0, md: '48px' },
                  marginBottom: { xs: 0, md: '9.75px' },
                }}
              />
              
              <FormControlLabel
                onPointerEnter={loadMultiCityFormRow}
                onPointerDown={loadMultiCityFormRow}
                control={
                  <IconButton
                    {...createGtmAttributes(GTM_CLICK_IDS.FLIGHT_SEARCH_TRIP_MULTICITY)}
                    onClick={() => changeTripType('multicity')}
                    sx={{
                      p: 0.2,
                      width: '20px',
                      height: '20px',
                      '&:hover': {
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={tripType === 'multicity' ? radioButtonFilled : radioButtonEmpty}
                      alt="radio button"
                      sx={{
                        width: '20px',
                        height: '20px',
                        display: 'block'
                      }}
                    />
                  </IconButton>
                }
                label={<Typography sx={{ 
                  fontFamily: 'var(--yovo-font-primary)',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '16px',
                  letterSpacing: '0.37px',
                  color: 'var(--yovo-grey-800)'
                }}>Multi City</Typography>}
                sx={{ 
                  gap: '8px',
                  marginTop: { xs: 0, md: '1.63px' },
                  marginRight: { xs: 0, md: '0.44px' },
                  marginBottom: { xs: 0, md: '9.75px' },
                }}
              />
              
              <Typography sx={{ 
                fontFamily: 'var(--yovo-font-primary)',
                fontSize: { xs: '12px', md: '14px' },
                color: 'var(--yovo-grey-800)',
                fontWeight: 600,
                lineHeight: '20px',
                letterSpacing: '0.4px',
                ml: 'auto',
                alignSelf: 'center',
                marginBottom: '9.75px',
                display: { xs: 'none', sm: 'none', md: 'block' }
              }}>
                Best fares guaranteed
              </Typography>
            </Box>
            )}

            {/* Search Form Fields — white inner box on mobile.
                For Multicity on mobile this becomes the single outer card from Figma (3008:105053):
                rounded 24px, 1px #D6D9E0 border, 16px padding, 16px gap between segments / Add City / Travelers / Class. */}
            <Box
              className={hideTripTypeSelector ? 'flight-seo-search-panel' : undefined}
              sx={{
              background: {
                xs: tripType === 'multicity' ? '#FBFBFB' : '#FFFFFF',
                sm: tripType === 'multicity' ? '#FBFBFB' : '#FFFFFF',
                md: 'transparent'
              },
              borderRadius: {
                xs: tripType === 'multicity' ? L.innerBoxRadius : L.innerBoxRadius,
                sm: tripType === 'multicity' ? L.innerBoxRadius : L.innerBoxRadius,
                md: 0
              },
              border: {
                xs: tripType === 'multicity'
                  ? '1px solid var(--neutrals-grey-400, #D6D9E0)'
                  : '1px solid var(--neutrals-grey-400, #DAD9E0)',
                sm: tripType === 'multicity'
                  ? '1px solid var(--neutrals-grey-400, #D6D9E0)'
                  : '1px solid var(--neutrals-grey-400, #DAD9E0)',
                md: 'none'
              },
              px: {
                xs: L.innerBoxPadding,
                sm: L.innerBoxPadding,
                md: 0
              },
              pt: {
                xs: L.innerBoxPaddingTop,
                sm: L.innerBoxPaddingTop,
                md: 0
              },
              pb: {
                xs: L.innerBoxPaddingBottom,
                sm: L.innerBoxPaddingBottom,
                md: 0
              },
              mb: { xs: '5px', sm: '5px', md: 0 },
              /* Use flex-column with gap ONLY for Multicity on mobile (Figma single card). 
                 Oneway/Roundtrip keeps its original block layout to avoid impacting field stacking. */
              display: {
                xs: tripType === 'multicity' ? 'flex' : 'block',
                sm: tripType === 'multicity' ? 'flex' : 'block',
                md: 'block'
              },
              flexDirection: {
                xs: tripType === 'multicity' ? 'column' : 'initial',
                sm: tripType === 'multicity' ? 'column' : 'initial',
                md: 'initial'
              },
              gap: {
                xs: tripType === 'multicity' ? '16px' : 0,
                sm: tripType === 'multicity' ? '16px' : 0,
                md: 0
              },
            }}>
            {/* Conditional Layout */}
            {tripType === 'multicity' ? (
              // Multi-city form - ensure same top spacing as one-way form
              
              <>
              {typeof journeySelector === 'function'
                ? journeySelector(
                    isSeoHeroDesktopUx
                      ? {
                          open: seoMcActivePanel === 'journey',
                          onOpenChange: (open) => requestSeoMcPanel(open ? 'journey' : null),
                        }
                      : undefined,
                  )
                : journeySelector}
              <Box sx={{
                /* On mobile this is just a flex column whose gap is provided by the parent (single Figma card).
                   On desktop, segments stack with normal margin-bottom from per-segment cards. */
                mb: { xs: 0, md: 1 },
                display: { xs: 'flex', md: 'block' },
                flexDirection: { xs: 'column', md: 'initial' },
                gap: { xs: '24px', md: 0 }
              }}>
                <Suspense
                  fallback={<MultiCityRowsPlaceholder count={multiCitySegments.length} isMobile={isMobile} />}
                >
                {multiCitySegments.map((segment, index) => (
                  <MultiCityFormRow 
                    key={segment.id}
                    segment={segment} 
                    index={index} 
                    segmentCalendarPrices={multiCityCalendarPrices[index] || emptyCalendarPrices}
                    onForceOpenHandled={handleForceOpenHandled}
                    forceOpenMulticityField={forceOpenMulticityField}
                    onRequestMobileAirportSheet={openMcMobileAirportSheet}
                    totalSegments={multiCitySegments.length}
                    isLastSegment={index === multiCitySegments.length - 1}
                    prevSegmentDate={index > 0 ? multiCitySegments[index - 1]?.date : null}
                    setMultiCitySegments={setMultiCitySegments}
                    inputRefs={inputRefs}
                    inputTimeoutRef={inputTimeoutRef}
                    isMobile={isMobile}
                    isSeoHeroDesktopUx={isSeoHeroDesktopUx}
                    seoMcActivePanel={isSeoHeroDesktopUx ? seoMcActivePanel : null}
                    onSeoMcPanelChange={isSeoHeroDesktopUx ? requestSeoMcPanel : undefined}
                    maxSelectableDate={maxSelectableDate}
                  />
                ))}
                </Suspense>
              </Box>
              </>
            ) : (
              // One-way and Round-trip form
              <Box
                className={
                  hideTripTypeSelector
                    ? `flight-search-strip flight-search-strip--ow-rt flight-select-hero-strip flight-select-hero-strip--${tripType === 'roundtrip' ? 'roundtrip' : 'oneway'} flight-seo-hero-strip`
                    : 'flight-search-strip flight-search-strip--ow-rt'
                }
                sx={{
                display: 'flex',
                gap: hideTripTypeSelector
                  ? { xs: '8px', md: 'clamp(8px, 1vw, 14px)' }
                  : { xs: 0, sm: 0, md: `${FLIGHT_SEARCH_STRIP_ROW_GAP} ${FLIGHT_SEARCH_STRIP_COLUMN_GAP}`, lg: '24px' },
                alignItems: { xs: 'center', sm: 'center', md: hideTripTypeSelector ? 'center' : 'flex-start' },
                justifyContent: 'flex-start',
                mb: 1,
                width: '100%',
                flexWrap: { xs: hideTripTypeSelector ? 'nowrap' : 'wrap', md: hideTripTypeSelector ? 'unset' : 'wrap', lg: hideTripTypeSelector ? 'unset' : 'nowrap' },
                minWidth: 0,
                boxSizing: 'border-box',
                position: hideTripTypeSelector ? undefined : 'relative',
                background: hideTripTypeSelector && isEmbedded ? 'transparent' : 'var(--yovo-base-white)',
                border: hideTripTypeSelector && isEmbedded
                  ? 'none'
                  : { xs: 'none', sm: 'none', md: '1px solid var(--yovo-grey-300)' },
                borderRadius: hideTripTypeSelector && isEmbedded ? 0 : { xs: 0, sm: 0, md: '24px' },
                pl: hideTripTypeSelector && isEmbedded ? 0 : { xs: 0, sm: 0, md: '16px', lg: '36px' },
                pr: getFlightSearchStripPaddingRight(hideTripTypeSelector, isEmbedded),
                py: hideTripTypeSelector && isEmbedded ? 0 : { xs: 0, sm: 0, md: '12px' }
              }}>
              {typeof journeySelector === 'function' ? journeySelector() : journeySelector}
              {/* From | swap | To — grouped on mobile/tablet for one aligned divider row */}
              <Box
                className={
                  hideTripTypeSelector
                    ? 'flight-select-hero-from-to-group'
                    : 'flight-search-strip-from-to-row'
                }
                sx={{
                  display: { xs: 'flex', lg: hideTripTypeSelector ? 'flex' : 'contents' },
                  flexDirection: 'row',
                  alignItems: { xs: 'stretch', md: hideTripTypeSelector ? 'stretch' : 'flex-start' },
                  width: { xs: '100%', md: 'auto' },
                  flex: { xs: '1 1 100%', md: hideTripTypeSelector ? 'unset' : '0 0 auto' },
                  position: hideTripTypeSelector ? 'relative' : 'static',
                  minWidth: hideTripTypeSelector ? 0 : undefined,
                  gap: hideTripTypeSelector ? 0 : { xs: 0, md: '12px', lg: 0 },
                }}
              >
              {/* FROM Field */}
              <Box
                className={
                  hideTripTypeSelector
                    ? 'flight-search-strip-airport-field flight-select-hero-airport-field flight-select-hero-airport-field--from'
                    : 'flight-search-strip-airport-field'
                }
                sx={{
                ...(hideTripTypeSelector
                  ? { flex: { xs: '1 1 0', sm: '1 1 0', md: '1 1 0' }, minWidth: { xs: 0, sm: 0, md: 0 } }
                  : getFlightSearchAirportFieldLayout(FLIGHT_SEARCH_FROM_FIELD_WIDTH)),
                display: 'flex',
                flexDirection: 'column',
                pt: { xs: '8px', sm: '8px', md: 0 },
                pb: { xs: '10px', sm: '10px', md: 0 },
                pl: { xs: '4px', sm: '4px', md: 0 },
                pr: { xs: '4px', sm: '4px', md: 0 },
                borderBottom: { xs: 'none', sm: 'none', md: 'none' },
                overflow: 'hidden'
              }}>
                <Typography sx={{ 
                  color: 'var(--Neutrals-base-black, #20202A)',
                  fontFamily: HOST_GROTESK_FAMILY,
                  fontSize: { xs: '14px', sm: '14px', md: '14px' },
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '20px',
                  letterSpacing: '0.15px',
                  mb: 0.3,
                  textAlign: 'left',
                }}>
                  From
                </Typography>
                <Box ref={desktopFromFieldRootRef} sx={{ position: 'relative', width: '100%' }}>
                  {/* Mobile tap interceptor — opens bottom sheet instead of MUI popper */}
                  {isMobile && (
                    <Box
                      sx={{ position: 'absolute', inset: 0, zIndex: 2, cursor: 'pointer' }}
                      onClick={(event) => {
                        event.stopPropagation();
                        mobileSheetFieldRef.current = 'from';
                        mobileHostRef.current?.openAirport('from');
                      }}
                    />
                  )}
                  {/* Popper stays open while loading so the in-panel search bar can drive the same API-backed state */}
                  <Autocomplete
                    freeSolo
                    loading={fromLoading}
                    options={fromOptions}
                    value={fromAirport}
                    open={!isMobile && fromDropdownOpen}
                    onOpen={() => {
                      if (isDesktopFromSplit) {
                        setFromSearchQuery('');
                      }
                      setFromDropdownOpen(true);
                      scheduleFocusMainFromSearch();
                      /* Only fetch when we genuinely have no list to show. If options already exist
                         (preloaded on mount or from a prior search) the popper opens populated and
                         "Searching airports..." never appears. */
                      const hasUserQuery = fromInput && fromInput.length >= 2;
                      const needsIndiaList = !hasUserQuery && fromOptions.length === 0;
                      if (needsIndiaList) {
                        const cached = indiaDefaultsCache.options;
                        if (cached && cached.length > 0) {
                          setFromOptions(cached);
                          setFromLoading(false);
                        } else {
                          setFromLoading(true);
                          fetchIndiaAirportDefaults()
                            .then((opts) => {
                              /* Only overwrite if the fetch actually returned data, otherwise keep
                                 whatever was there (prevents wiping the panel into an empty bar). */
                              if (opts && opts.length > 0) setFromOptions(opts);
                            })
                            .finally(() => setFromLoading(false));
                        }
                      } else {
                        /* Avoid stuck "Searching airports..." when reopening with a selection or
                           with options already populated and no new fetch. */
                        setFromLoading(false);
                      }
                    }}
                    onClose={() => {
                      if (inputTimeoutRef.current['main-from-search']) {
                        clearTimeout(inputTimeoutRef.current['main-from-search']);
                        inputTimeoutRef.current['main-from-search'] = null;
                      }
                      setFromLoading(false);
                      if (isDesktopFromSplit) {
                        setFromSearchQuery('');
                      }
                      setFromDropdownOpen(false);
                    }}
                    onChange={(_, newValue) => {
                      if (newValue) {
                        // Prevent selecting same airport as "To"
                        if (toAirport && newValue.value === toAirport.value) {
                          // Show warning and prevent selection
                          alert(`Please select a different airport. "${newValue.name} (${newValue.iataCode})" is already selected as destination.`);
                          return;
                        }
                        setFromAirport(newValue);
                        const cityName = newValue.cityName || newValue.address?.cityName || '';
                        const countryName = newValue.countryName || newValue.address?.countryName || '';
                        const iataCode = newValue.iataCode || newValue.value || '';
                        const cleanName = `${cityName} ${iataCode}`;
                        setFromInput(cleanName);
                        if (isDesktopFromSplit) {
                          setFromSearchQuery('');
                        }
                        setFromDropdownOpen(false);
                        
                        // Auto-open and focus on "to" dropdown for one-way and round-trip trips
                        if ((tripType === 'oneway' || tripType === 'roundtrip') && !toAirport?.value) {
                          setTimeout(() => {
                            setToDropdownOpen(true);
                            // Load options if needed
                            if (!toInput || toInput.length < 2) {
                              setToLoading(true);
                              searchAirports('India')
                                .then(async res => {
                                  const options = (res.data || []).map(a => {
                                    if (!a.iataCode) return null;
                                    return {
                                      ...a,
                                      value: a.iataCode,
                                      label: `${a.name} (${a.iataCode}) - ${a.address?.cityName || a.cityName}, ${a.address?.countryName || a.countryName}`,
                                      cityName: a.address?.cityName || a.cityName,
                                      countryName: a.address?.countryName || a.countryName
                                    };
                                  }).filter(Boolean);
                                  const uniqueOptions = deduplicateAirports(options);
                                  setToOptions(uniqueOptions.slice(0, 20));
                                })
                                .catch(error => {
                                  console.error('Error fetching popular airports:', error);
                                  setToOptions([]);
                                })
                                .finally(() => setToLoading(false));
                            }
                            // Focus the to input field
                            setTimeout(() => {
                              const toInputField = inputRefs.current['main-to'] || document.querySelector('input[placeholder*="Select Destination"], input[placeholder*="Destination"]');
                              if (toInputField) {
                                toInputField.focus();
                              }
                            }, 50);
                          }, 100);
                        }
                      }
                    }}
                    inputValue={isMobile ? (fromAirport ? formatAirportStripDisplay(fromAirport) : '') : fromInput}
                    onInputChange={(_, newInput, reason) => {
                      if (isMobile) return;
                      if (isDesktopFromSplit && reason === 'input') {
                        return;
                      }
                      if (reason === 'input' || reason === 'clear') {
                        console.log('✏️ FROM input changed:', newInput, 'reason:', reason);
                        setFromInput(newInput);
                        // Clear airport state when input is cleared
                        if (!newInput || newInput.trim() === '') {
                          setFromAirport(null);
                        }
                      }
                    }}
                    getOptionLabel={option => option?.isBanner ? option.text : (option?.label || '')}
                    filterOptions={(options) => buildAirportFilterResult(options, null)}
                    popupIcon={null}
                    autoHighlight={true}
                    autoSelect={false}
                    selectOnFocus={false}
                    clearOnBlur={false}
                    handleHomeEndKeys={false}
                    noOptionsText=""
                    loadingText="Searching airports..."
                    slotProps={{
                      popper: {
                        sx: {
                          width: { xs: 'calc(100vw - 32px) !important', sm: 'calc(100vw - 32px) !important', md: '392px !important' },
                          maxWidth: '100vw',
                        },
                        placement: 'bottom-start',
                        /* Force downward rendering at every breakpoint — never flip to top.
                           `flip:false` locks placement to `bottom-start`; `preventOverflow.mainAxis:false`
                           stops Popper from sliding the panel upward when space is tight.
                           The listbox already has maxHeight + overflowY:auto, so it stays scrollable inside the viewport. */
                        modifiers: [
                          { name: 'flip', enabled: false },
                          { name: 'preventOverflow', enabled: true, options: { boundary: 'viewport', padding: 16, mainAxis: false, altAxis: true } },
                        ],
                      },
                      paper: {
                        sx: {
                          minWidth: 0,
                          maxWidth: '100%',
                          width: '100%',
                          background: 'var(--yovo-grey-100, #F5F5F7)',
                          borderRadius: { xs: '12px', md: '16px' },
                          boxShadow: '0 0 12px rgba(0,0,0,0.12)',
                          border: 'none',
                          p: { xs: '10px', md: '16px' },
                          pb: { xs: '10px', md: '14px' },
                          overflowX: 'hidden',
                          overflowY: 'hidden',
                          '& .MuiAutocomplete-listbox': {
                            p: 0, pt: { xs: '8px', md: '12px' },
                            maxHeight: { xs: '40vh', md: '272px' },
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            WebkitOverflowScrolling: 'touch',
                            '&::-webkit-scrollbar': { display: 'none' },
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                          },
                          '& .MuiAutocomplete-option': {
                            p: { xs: '10px 12px !important', md: '12px 16px !important' },
                            minHeight: { xs: '44px !important', md: 'auto !important' },
                            whiteSpace: 'normal !important',
                            overflowX: 'hidden !important',
                            width: '100% !important',
                            boxSizing: 'border-box !important',
                            backgroundColor: 'transparent !important',
                            '&:hover': { backgroundColor: '#EDEDF0 !important' },
                            '&[aria-selected="true"]': { backgroundColor: 'transparent !important' },
                          },
                          '& .MuiAutocomplete-option[data-separator="true"]': {
                            backgroundColor: 'transparent !important',
                            p: '0 16px !important',
                            minHeight: 'auto !important',
                            cursor: 'default !important',
                            pointerEvents: 'none !important',
                            '&:hover': { backgroundColor: 'transparent !important' },
                          },
                        }
                      }
                    }}
                    sx={{
                      '& .MuiAutocomplete-inputRoot': {
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        minHeight: '40px',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#ab2645',
                        },
                        '&.Mui-focused': {
                          borderColor: '#e0e0e0',
                          boxShadow: 'none',
                        }
                      },
                      '& .MuiAutocomplete-input': {
                        padding: '0 !important',
                        paddingLeft: '0 !important',
                        paddingRight: '0 !important',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        color: '#333',
                      width: '100% !important',
                      minWidth: '100% !important',
                      overflow: 'hidden !important',
                      textOverflow: 'ellipsis !important',
                      whiteSpace: 'nowrap !important'
                      },
                      '& .MuiAutocomplete-endAdornment': {
                        display: 'none',
                      },
                      '& .MuiAutocomplete-popupIndicator': {
                        display: 'none',
                      },
                      '& .MuiAutocomplete-clearIndicator': {
                        display: 'none',
                      },
                      '& .MuiInputLabel-root': {
                        padding: '0 6px 0 0',
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        minHeight: '28px',
                        background: 'transparent',
                        border: 'none',
                        padding: '0 !important',
                        paddingLeft: '0 !important',
                        paddingRight: '0 !important',
                        '& fieldset': {
                          border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none',
                          boxShadow: 'none',
                        },
                      },
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={ORIGIN_PLACEHOLDER}
                        size="small"
                        inputProps={{
                          ...params.inputProps,
                          readOnly: isDesktopFromSplit || isMobile,
                          autoComplete: 'off',
                          onBlur: (event) => {
                            if (
                              isDesktopFromSplit &&
                              mainFromSearchInputRef.current &&
                              (event.relatedTarget === mainFromSearchInputRef.current ||
                                (event.relatedTarget != null &&
                                  mainFromSearchInputRef.current.contains(event.relatedTarget)))
                            ) {
                              /* Focus moved to in-dropdown search; do not run MUI blur (would refocus main or close). */
                              return;
                            }
                            params.inputProps.onBlur?.(event);
                          },
                          ref: (el) => {
                            if (el) {
                              inputRefs.current['main-from'] = el;
                            }
                            // Also call the original ref if it exists
                            if (params.inputProps?.ref) {
                              if (typeof params.inputProps.ref === 'function') {
                                params.inputProps.ref(el);
                              } else {
                                params.inputProps.ref.current = el;
                              }
                            }
                          }
                        }}
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            minHeight: '36px',
                            height: '36px',
                            '&.Mui-focused fieldset': {
                              borderColor: '#e0e0e0',
                              boxShadow: 'none',
                            },
                          },
                          '& .MuiInputBase-input': {
                            overflow: 'hidden',
                            color: 'var(--Neutrals-grey-700, var(--yovo-grey-700))',
                            '&:not(:placeholder-shown)': {
                              color: 'var(--Neutrals-base-black, #20202A)',
                            },
                            textOverflow: 'ellipsis',
                            fontFamily: HOST_GROTESK_FAMILY,
                            fontSize: '1rem',
                            fontStyle: 'normal',
                            fontWeight: 500,
                            lineHeight: '2rem',
                            width: '100%',
                            whiteSpace: 'nowrap',
                            '&::placeholder': {
                              color: 'var(--Neutrals-grey-700, var(--yovo-grey-700))',
                              opacity: 1
                            }
                          }
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                    ListboxComponent={MainFromAutocompleteListbox}
                    renderOption={(props, option) => {
                      const { key, ...rest } = props;
                      if (option.isSeparator) {
                        return (
                          <li key={key} {...rest} data-separator="true" className="flight-dropdown-separator">
                            <span>{option.label}</span>
                          </li>
                        );
                      }
                      
                      const cityName = option.isNearestAirport 
                        ? option.address?.cityName || option.cityName
                        : (option.cityName || option.address?.cityName);
                      const countryName = option.isNearestAirport
                        ? option.address?.countryName || option.countryName
                        : (option.countryName || option.address?.countryName || 'India');
                      const airportName = option.name || '';
                      const displayCity = cityName ? `${cityName}, ${countryName}` : countryName;
                      
                      return (
                        <li key={key} {...rest} data-nearest={option.isNearestAirport ? 'true' : 'false'} className="flight-dropdown-option" style={{
                          paddingLeft: option.isNearestAirport ? '43px' : '16px',
                        }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#EDEDF0'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <img 
                            src={flight_icon_homepage} 
                            alt=""
                            className="flight-dropdown-option-icon"
                          />
                          <div className="flight-dropdown-option-content">
                            <div className="flight-dropdown-option-row">
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="flight-dropdown-option-city">
                                  {displayCity}
                                </div>
                                <div className="flight-dropdown-option-airport">
                                  {option.isNearestAirport 
                                    ? `${option.distanceKm || option.distanceFromCity || ''} KMs from ${option.isNearestTo || cityName || ''}`
                                    : airportName
                                  }
                                </div>
                              </div>
                              <span className="flight-dropdown-option-code">
                                {option.iataCode}
                              </span>
                            </div>
                          </div>
                        </li>
                      );
                    }}
                  />
                </Box>
              </Box>

              {/* Swap — vertical divider + circle (mobile/tablet); compact on desktop */}
              <Box
                className={
                  hideTripTypeSelector
                    ? 'flight-form-swap-col flight-select-hero-swap-field'
                    : 'flight-form-swap-col'
                }
              >
                <span className="flight-form-swap-divider" aria-hidden="true" />
                <button
                  type="button"
                  className="flight-form-swap"
                  onClick={() => {
                    const tempAirport = fromAirport;
                    const tempInput = fromInput;
                    const tempOptions = fromOptions;
                    const tempLoading = fromLoading;
                    const tempDropdownOpen = fromDropdownOpen;
                    
                    setFromAirport(toAirport);
                    setFromInput(toInput);
                    setFromOptions(toOptions);
                    setFromLoading(toLoading);
                    setFromDropdownOpen(toDropdownOpen);
                    if (isDesktopFromSplit) {
                      setFromSearchQuery('');
                    }
                    
                    setToAirport(tempAirport);
                    setToInput(tempInput);
                    setToOptions(tempOptions);
                    setToLoading(tempLoading);
                    setToDropdownOpen(tempDropdownOpen);
                    if (isDesktopFromSplit) {
                      setToSearchQuery('');
                    }
                  }}
                  aria-label="Swap origin and destination"
                >
                  <img src={swap_icon} alt="" className="flight-form-swap-icon" />
                </button>
              </Box>

              {/* TO Field */}
              <Box
                className={
                  hideTripTypeSelector
                    ? 'flight-search-strip-airport-field flight-select-hero-airport-field flight-select-hero-airport-field--to'
                    : 'flight-search-strip-airport-field'
                }
                sx={{
                ...(hideTripTypeSelector
                  ? { flex: { xs: '1 1 0', sm: '1 1 0', md: '1 1 0' }, minWidth: { xs: 0, sm: 0, md: 0 } }
                  : getFlightSearchAirportFieldLayout(FLIGHT_SEARCH_TO_FIELD_WIDTH)),
                display: 'flex',
                flexDirection: 'column',
                pt: { xs: '8px', sm: '8px', md: 0 },
                pb: { xs: '10px', sm: '10px', md: 0 },
                pl: { xs: '4px', sm: '4px', md: 0 },
                pr: { xs: '4px', sm: '4px', md: 0 },
                borderBottom: { xs: 'none', sm: 'none', md: 'none' },
                overflow: 'hidden'
              }}>
                <Typography sx={{ 
                  color: 'var(--Neutrals-base-black, #20202A)',
                  fontFamily: HOST_GROTESK_FAMILY,
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '20px',
                  letterSpacing: '0.15px',
                  mb: 0.2,
                  textAlign: 'left',
                }}>
                  To
                </Typography>
                <Box ref={desktopToFieldRootRef} sx={{ position: 'relative', width: '100%' }}>
                  {/* Mobile tap interceptor — opens bottom sheet instead of MUI popper */}
                  {isMobile && (
                    <Box
                      sx={{ position: 'absolute', inset: 0, zIndex: 2, cursor: 'pointer' }}
                      onClick={(event) => {
                        event.stopPropagation();
                        mobileSheetFieldRef.current = 'to';
                        mobileHostRef.current?.openAirport('to');
                      }}
                    />
                  )}
                  <Autocomplete
                    freeSolo
                    loading={toLoading}
                    options={toOptions}
                    value={toAirport}
                    open={!isMobile && toDropdownOpen}
                    onOpen={() => {
                      if (isDesktopFromSplit) {
                        setToSearchQuery('');
                      }
                      setToDropdownOpen(true);
                      scheduleFocusMainToSearch();
                      /* Only fetch when no list to show; otherwise reuse cached / preloaded options
                         so the popper opens populated and "Searching airports..." never appears. */
                      const hasUserQuery = toInput && toInput.length >= 2;
                      const needsIndiaList = !hasUserQuery && toOptions.length === 0;
                      if (needsIndiaList) {
                        const cached = indiaDefaultsCache.options;
                        if (cached && cached.length > 0) {
                          setToOptions(cached);
                          setToLoading(false);
                        } else {
                          setToLoading(true);
                          fetchIndiaAirportDefaults()
                            .then((opts) => {
                              if (opts && opts.length > 0) setToOptions(opts);
                            })
                            .finally(() => setToLoading(false));
                        }
                      } else {
                        setToLoading(false);
                      }
                    }}
                    onClose={() => {
                      if (inputTimeoutRef.current['main-to-search']) {
                        clearTimeout(inputTimeoutRef.current['main-to-search']);
                        inputTimeoutRef.current['main-to-search'] = null;
                      }
                      setToLoading(false);
                      if (isDesktopFromSplit) {
                        setToSearchQuery('');
                      }
                      setToDropdownOpen(false);
                    }}
                    onChange={(_, newValue) => {
                      if (newValue) {
                        // Prevent selecting same airport as "From"
                        if (fromAirport && newValue.value === fromAirport.value) {
                          // Show warning and prevent selection
                          alert(`Please select a different airport. "${newValue.name} (${newValue.iataCode})" is already selected as origin.`);
                          return;
                        }
                        setToAirport(newValue);
                        const cityName = newValue.cityName || newValue.address?.cityName || '';
                        const countryName = newValue.countryName || newValue.address?.countryName || '';
                        const iataCode = newValue.iataCode || newValue.value || '';
                        const cleanName = `${cityName} ${iataCode}`;
                        setToInput(cleanName);
                        if (isDesktopFromSplit) {
                          setToSearchQuery('');
                        }
                        setToDropdownOpen(false);
                        
                        // Auto-open date picker for one-way and round-trip trips
                        if ((tripType === 'oneway' || tripType === 'roundtrip') && !startDate) {
                          setTimeout(() => openDatePicker('departure'), 100);
                        }
                      }
                    }}
                    inputValue={isMobile ? (toAirport ? formatAirportStripDisplay(toAirport) : '') : toInput}
                    onInputChange={(_, newInput, reason) => {
                      if (isMobile) return;
                      if (isDesktopFromSplit && reason === 'input') {
                        return;
                      }
                      if (reason === 'input' || reason === 'clear') {
                        setToInput(newInput);
                        // Clear airport state when input is cleared
                        if (!newInput || newInput.trim() === '') {
                          setToAirport(null);
                        }
                      }
                    }}
                    getOptionLabel={option => option?.isBanner ? option.text : (option?.label || '')}
                    filterOptions={(options) => buildAirportFilterResult(options, fromAirport?.value || null)}
                    popupIcon={null}
                    autoHighlight={true}
                    autoSelect={false}
                    selectOnFocus={false}
                    clearOnBlur={false}
                    handleHomeEndKeys={false}
                    noOptionsText=""
                    loadingText="Searching airports..."
                    slotProps={{
                      popper: {
                        sx: {
                          width: { xs: 'calc(100vw - 32px) !important', sm: 'calc(100vw - 32px) !important', md: '392px !important' },
                          maxWidth: '100vw',
                        },
                        placement: 'bottom-start',
                        /* Force downward rendering at every breakpoint — never flip to top.
                           `flip:false` locks placement to `bottom-start`; `preventOverflow.mainAxis:false`
                           stops Popper from sliding the panel upward when space is tight.
                           The listbox already has maxHeight + overflowY:auto, so it stays scrollable inside the viewport. */
                        modifiers: [
                          { name: 'flip', enabled: false },
                          { name: 'preventOverflow', enabled: true, options: { boundary: 'viewport', padding: 16, mainAxis: false, altAxis: true } },
                        ],
                      },
                      paper: {
                        sx: {
                          minWidth: 0,
                          maxWidth: '100%',
                          width: '100%',
                          background: 'var(--yovo-grey-100, #F5F5F7)',
                          borderRadius: { xs: '12px', md: '16px' },
                          boxShadow: '0 0 12px rgba(0,0,0,0.12)',
                          border: 'none',
                          p: { xs: '10px', md: '16px' },
                          pb: { xs: '10px', md: '14px' },
                          overflowX: 'hidden',
                          overflowY: 'hidden',
                          '& .MuiAutocomplete-listbox': {
                            p: 0, pt: { xs: '8px', md: '12px' },
                            maxHeight: { xs: '40vh', md: '272px' },
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            WebkitOverflowScrolling: 'touch',
                            '&::-webkit-scrollbar': { display: 'none' },
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                          },
                          '& .MuiAutocomplete-option': {
                            p: { xs: '10px 12px !important', md: '12px 16px !important' },
                            minHeight: { xs: '44px !important', md: 'auto !important' },
                            whiteSpace: 'normal !important',
                            overflowX: 'hidden !important',
                            width: '100% !important',
                            boxSizing: 'border-box !important',
                            backgroundColor: 'transparent !important',
                            '&:hover': { backgroundColor: '#EDEDF0 !important' },
                            '&[aria-selected="true"]': { backgroundColor: 'transparent !important' },
                          },
                          '& .MuiAutocomplete-option[data-separator="true"]': {
                            backgroundColor: 'transparent !important',
                            p: '0 16px !important',
                            minHeight: 'auto !important',
                            cursor: 'default !important',
                            pointerEvents: 'none !important',
                            '&:hover': { backgroundColor: 'transparent !important' },
                          },
                        }
                      }
                    }}
                    sx={{
                      '& .MuiAutocomplete-inputRoot': {
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        minHeight: '40px',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#ab2645',
                        },
                        '&.Mui-focused': {
                          borderColor: '#e0e0e0',
                          boxShadow: 'none',
                        }
                      },
                      '& .MuiAutocomplete-input': {
                        padding: '0 !important',
                        paddingLeft: '0 !important',
                        paddingRight: '0 !important',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        color: '#333',
                      width: '100% !important',
                      minWidth: '100% !important',
                      overflow: 'hidden !important',
                      textOverflow: 'ellipsis !important',
                      whiteSpace: 'nowrap !important'
                      },
                      '& .MuiAutocomplete-endAdornment': {
                        display: 'none',
                      },
                      '& .MuiAutocomplete-popupIndicator': {
                        display: 'none',
                      },
                      '& .MuiAutocomplete-clearIndicator': {
                        display: 'none',
                      },
                      '& .MuiInputLabel-root': {
                        padding: '0 6px 0 0',
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        minHeight: '28px',
                        background: 'transparent',
                        border: 'none',
                        padding: '0 !important',
                        paddingLeft: '0 !important',
                        paddingRight: '0 !important',
                        '& fieldset': {
                          border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none',
                          boxShadow: 'none',
                        },
                      },
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={DEST_PLACEHOLDER}
                        size="small"
                        inputProps={{
                          ...params.inputProps,
                          readOnly: isDesktopFromSplit || isMobile,
                          autoComplete: 'off',
                          onBlur: (event) => {
                            if (
                              isDesktopFromSplit &&
                              mainToSearchInputRef.current &&
                              (event.relatedTarget === mainToSearchInputRef.current ||
                                (event.relatedTarget != null &&
                                  mainToSearchInputRef.current.contains(event.relatedTarget)))
                            ) {
                              return;
                            }
                            params.inputProps.onBlur?.(event);
                          },
                          ref: (el) => {
                            if (el) {
                              inputRefs.current['main-to'] = el;
                            }
                            if (params.inputProps?.ref) {
                              if (typeof params.inputProps.ref === 'function') {
                                params.inputProps.ref(el);
                              } else {
                                params.inputProps.ref.current = el;
                              }
                            }
                          }
                        }}
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            minHeight: '36px',
                            height: '36px',
                            '&.Mui-focused fieldset': {
                              borderColor: '#e0e0e0',
                              boxShadow: 'none',
                            },
                          },
                          '& .MuiInputBase-input': {
                            overflow: 'hidden',
                            color: 'var(--Neutrals-grey-700, var(--yovo-grey-700))',
                            '&:not(:placeholder-shown)': {
                              color: 'var(--Neutrals-base-black, #20202A)',
                            },
                            textOverflow: 'ellipsis',
                            fontFamily: HOST_GROTESK_FAMILY,
                            fontSize: '1rem',
                            fontStyle: 'normal',
                            fontWeight: 500,
                            lineHeight: '2rem',
                            width: '100%',
                            whiteSpace: 'nowrap',
                            '&::placeholder': {
                              color: 'var(--Neutrals-grey-700, var(--yovo-grey-700))',
                              opacity: 1
                            }
                          }
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                    ListboxComponent={MainToAutocompleteListbox}
                    renderOption={(props, option) => {
                      const { key, ...rest } = props;
                      if (option.isSeparator) {
                        return (
                          <li key={key} {...rest} data-separator="true" className="flight-dropdown-separator">
                            <span>{option.label}</span>
                          </li>
                        );
                      }
                      
                      const cityName = option.isNearestAirport 
                        ? option.address?.cityName || option.cityName
                        : (option.cityName || option.address?.cityName);
                      const countryName = option.isNearestAirport
                        ? option.address?.countryName || option.countryName
                        : (option.countryName || option.address?.countryName || 'India');
                      const airportName = option.name || '';
                      const displayCity = cityName ? `${cityName}, ${countryName}` : countryName;
                      
                      return (
                        <li key={key} {...rest} data-nearest={option.isNearestAirport ? 'true' : 'false'} className="flight-dropdown-option" style={{
                          paddingLeft: option.isNearestAirport ? '43px' : '16px',
                        }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#EDEDF0'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <img 
                            src={flight_icon_homepage} 
                            alt=""
                            className="flight-dropdown-option-icon"
                          />
                          <div className="flight-dropdown-option-content">
                            <div className="flight-dropdown-option-row">
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="flight-dropdown-option-city">
                                  {displayCity}
                                </div>
                                <div className="flight-dropdown-option-airport">
                                  {option.isNearestAirport 
                                    ? `${option.distanceKm || option.distanceFromCity || ''} KMs from ${option.isNearestTo || cityName || ''}`
                                    : airportName
                                  }
                                </div>
                              </div>
                              <span className="flight-dropdown-option-code">
                                {option.iataCode}
                              </span>
                            </div>
                          </div>
                        </li>
                      );
                    }}
                  />
                </Box>
              </Box>
              </Box>{/* end flight-search-strip-from-to-row */}

              {isSeoHeroDesktopUx && SeoHeroDesktopFields ? (
                <SeoHeroDesktopFields
                  tripType={tripType}
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  maxSelectableDate={maxSelectableDate}
                  renderDepartureDayContents={renderDepartureDayContents}
                  renderReturnDayContents={renderReturnDayContents}
                  numAdults={numAdults}
                  numChildren={numChildren}
                  numInfants={numInfants}
                  setNumAdults={setNumAdults}
                  setNumChildren={setNumChildren}
                  setNumInfants={setNumInfants}
                  totalPassengers={totalPassengers}
                  isSpecialFare={isSpecialFare}
                  fareType={fareType}
                  onPassengersTouched={() => setPassengersTouched(true)}
                />
              ) : (
              <>
              {/* Travel date(s): one-way single field; round-trip separate Departure + Return. */}
              {tripType === 'roundtrip' ? (
                <Box
                  className="flight-search-strip-dates-row"
                  sx={{
                    flex: { xs: '1 1 100%', sm: '1 1 100%', md: '0 0 auto' },
                    flexShrink: { md: 0 },
                    minWidth: { xs: '100%', sm: '100%', md: 'auto' },
                    pt: { xs: '10px', sm: '10px', md: 0 },
                    pb: { xs: '8px', sm: '8px', md: 0 },
                    pl: { xs: '4px', sm: '4px', md: 0 },
                    borderBottom: { xs: 'none', md: 'none' },
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'stretch',
                    columnGap: { md: '16px' },
                  }}
                  data-date-picker
                >
                  {['departure', 'return'].map((field) => {
                    const isDeparture = field === 'departure';
                    const selectedDate = isDeparture ? startDate : endDate;
                    const label = isDeparture ? 'Departure' : 'Return';
                    const displayValue = selectedDate
                      ? formatStripDate(selectedDate, isMobile)
                      : 'Add Dates';
                    return (
                      <Box
                        key={field}
                        className="flight-search-strip-date-field"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          flex: { xs: '1 1 0', md: '0 0 auto' },
                          flexShrink: { md: 0 },
                          minWidth: { xs: 0, md: FLIGHT_SEARCH_DATE_MIN_WIDTH },
                          pt: 0,
                          pb: 0,
                          pl: isDeparture ? 0 : { xs: '8px', md: 0 },
                          borderBottom: 'none',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: -0.5 }}>
                          <Typography sx={{
                            color: 'var(--Neutrals-base-black, #20202A)',
                            fontFamily: HOST_GROTESK_FAMILY,
                            fontSize: '14px',
                            fontWeight: 400,
                            lineHeight: '20px',
                            letterSpacing: '0.15px',
                          }}>
                            {label}
                          </Typography>
                          <KeyboardArrowDownIcon sx={{ fontSize: '18px', color: '#545665', ml: 0.3 }} />
                        </Box>
                        <TextField
                          placeholder="Add Dates"
                          size="small"
                          fullWidth
                          autoComplete="off"
                          readOnly
                          inputProps={{
                            readOnly: true,
                            style: { cursor: 'pointer', caretColor: 'transparent' },
                          }}
                          value={displayValue}
                          onClick={(e) => {
                            e.preventDefault();
                            if (isMobile) {
                              mobileHostRef.current?.openDate(field, { startDate, endDate });
                              return;
                            }
                            if (showDatePicker && activeDateField === field) {
                              setShowDatePicker(false);
                            } else {
                              openDatePicker(field);
                            }
                          }}
                          onFocus={(e) => e.target.blur()}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 4,
                              background: 'transparent',
                              border: 'none',
                              paddingLeft: 0,
                              cursor: 'pointer',
                              '& fieldset': { border: 'none' },
                              '&.Mui-focused fieldset': { border: 'none', boxShadow: 'none' },
                            },
                            '& .MuiInputBase-input': getFlightSearchDateInputSx({ hasDate: Boolean(selectedDate) }),
                          }}
                        />
                      </Box>
                    );
                  })}
                  {showDatePicker && !isMobile && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#fff',
                        border: 'none',
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        p: 1.2,
                        pt: 1.5,
                        mt: 0.5,
                        minWidth: '600px',
                        width: 'auto',
                        maxWidth: '650px',
                        overflowX: 'visible',
                      }}
                    >
                      {renderMainSegmentDateCalendars()}
                    </Box>
                  )}
                </Box>
              ) : (
                <Box className="flight-search-strip-date-field" sx={{ ...getFlightSearchDateFieldLayout(), display: 'flex', flexDirection: 'column', pt: { xs: '10px', sm: '10px', md: 0 }, pb: { xs: '8px', sm: '8px', md: 0 }, pl: { xs: '4px', sm: '4px', md: 0 }, borderBottom: { xs: '1px solid #E8E9ED', sm: '1px solid #E8E9ED', md: 'none' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: -0.5 }}>
                    <Typography sx={{
                      color: 'var(--Neutrals-base-black, #20202A)',
                      fontFamily: HOST_GROTESK_FAMILY,
                      fontSize: '14px',
                      fontWeight: 400,
                      lineHeight: '20px',
                      letterSpacing: '0.15px',
                    }}>
                      Travel Date
                    </Typography>
                    <KeyboardArrowDownIcon sx={{ fontSize: '18px', color: '#545665', ml: 0.3 }} />
                  </Box>
                  <Box sx={{ position: 'relative', width: '100%', margin: 0, padding: 0 }} data-date-picker>
                    <TextField
                      placeholder="Add Dates"
                      size="small"
                      fullWidth
                      autoComplete="off"
                      readOnly
                      inputProps={{
                        readOnly: true,
                        style: { cursor: 'pointer', caretColor: 'transparent' },
                      }}
                      value={startDate
                        ? startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : 'Add Dates'}
                      onClick={(e) => {
                        e.preventDefault();
                        if (isMobile) {
                          mobileHostRef.current?.openDate('departure', { startDate, endDate });
                          return;
                        }
                        if (showDatePicker) {
                          setShowDatePicker(false);
                        } else {
                          openDatePicker('departure');
                        }
                      }}
                      onFocus={(e) => e.target.blur()}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 4,
                          background: 'transparent',
                          border: 'none',
                          paddingLeft: 0,
                          cursor: 'pointer',
                          '& fieldset': { border: 'none' },
                          '&.Mui-focused fieldset': { border: 'none', boxShadow: 'none' },
                        },
                        '& .MuiInputBase-input': getFlightSearchDateInputSx({ hasDate: Boolean(startDate) }),
                      }}
                    />
                    {showDatePicker && !isMobile && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: '#fff',
                          border: 'none',
                          borderRadius: 3,
                          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                          zIndex: 1000,
                          p: 1.2,
                          pt: 1.5,
                          mt: 0.5,
                          minWidth: '600px',
                          width: 'auto',
                          maxWidth: '650px',
                          overflowX: 'visible',
                        }}
                      >
                        {renderMainSegmentDateCalendars()}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Travelers Field */}
              <Box className="flight-search-strip-travelers-field" sx={{ ...getFlightSearchTravelersFieldLayout(), display: 'flex', flexDirection: 'column', pt: { xs: '10px', sm: '10px', md: 0 }, pb: { xs: '8px', sm: '8px', md: 0 }, pl: { xs: '4px', sm: '4px', md: 0 }, borderBottom: { xs: '1px solid #E8E9ED', sm: '1px solid #E8E9ED', md: 'none' } }}>
                <Typography sx={{ 
                  color: 'var(--Neutrals-base-black, #20202A)',
                  fontFamily: HOST_GROTESK_FAMILY,
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '20px',
                  letterSpacing: '0.15px',
                  mb: -0.5,
                  textAlign: 'left'
                }}>
                  Travelers
                </Typography>
                <Box sx={{ position: 'relative', width: '100%', margin: 0, padding: 0 }} data-passenger-dropdown>
                  <TextField
                    value={passengers}
                    size="small"
                    fullWidth
                    autoComplete="off"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        minHeight: '28px',
                        background: 'transparent',
                        border: 'none',
                        '& fieldset': {
                          border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none',
                          boxShadow: 'none',
                        },
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '1rem',
                        fontWeight: 500,
                        color: '#20202A',
                        fontFamily: HOST_GROTESK_FAMILY,
                        cursor: 'pointer',
                        paddingLeft: 0
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      readOnly: true,
                      'aria-label': 'Travelers',
                      style: { cursor: 'pointer' }
                    }}
                    onClick={() => setShowPassengerDropdown(!showPassengerDropdown)}
                    readOnly
                  />
                  {showPassengerDropdown && !isMobile && (
                    <Box
                      data-passenger-dropdown
                      className="passenger-dropdown"
                      sx={{
                        position: { xs: 'fixed', md: 'absolute' },
                        top: { xs: '50%', md: '100%' },
                        left: { xs: '50%', md: '50%' },
                        transform: { xs: 'translate(-50%, -50%)', md: 'translateX(-50%)' },
                        zIndex: 1401,
                        isolation: 'isolate',
                        background: '#F5F5F7',
                        border: 'none',
                        borderRadius: { xs: '14px', md: '16px' },
                        boxShadow: '0 0 12px rgba(0,0,0,0.12)',
                        p: { xs: '12px', md: '16px' },
                        pb: { xs: '12px', md: '16px' },
                        pt: { xs: '10px', md: '12px' },
                        mt: { xs: 0, md: 0.5 },
                        minWidth: { xs: '240px', md: '300px' },
                        width: { xs: 'calc(100vw - 64px)', md: 'auto' },
                        maxWidth: { xs: '320px', md: '380px' }
                      }}
                      onClick={e => e.stopPropagation()}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '20px', md: '32px' } }}>
                        {/* Adults Section */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '8px', md: '12px' } }}>
                          <Typography className="passenger-dropdown-label">
                            Adults
                          </Typography>
                          <Box className="passenger-counter-bar">
                            <Button
                              className="passenger-counter-btn"
                              disabled={numAdults <= 1 || numInfants >= numAdults}
                              onClick={() => {
                                const newAdults = numAdults - 1;
                                setNumAdults(newAdults);
                                if (numInfants > newAdults) setNumInfants(newAdults);
                                setPassengersTouched(true);
                              }}
                            >
                              −
                            </Button>
                            <Typography className="passenger-counter-value" sx={{ color: '#20202A' }}>
                              {numAdults}
                            </Typography>
                            <Button
                              className="passenger-counter-btn"
                              disabled={totalPassengers >= 9}
                              onClick={() => { setNumAdults(numAdults + 1); setPassengersTouched(true); }}
                            >
                              +
                            </Button>
                          </Box>
                        </Box>

                        {/* Children Section */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '8px', md: '12px' }, opacity: isSpecialFare ? 0.45 : 1 }}>
                          <Typography className="passenger-dropdown-label">
                            Children (2Ys - 12Ys)
                          </Typography>
                          {isSpecialFare && (
                            <Typography sx={{ fontSize: '0.65rem', color: '#E65100', mt: -0.5, fontFamily: HOST_GROTESK_FAMILY }}>
                              Not available for {fareType === 'student' ? 'Student' : 'Senior Citizen'} fare
                            </Typography>
                          )}
                          <Box className="passenger-counter-bar">
                            <Button
                              className="passenger-counter-btn"
                              disabled={numChildren <= 0 || isSpecialFare}
                              onClick={() => { if (!isSpecialFare) { setNumChildren(numChildren - 1); setPassengersTouched(true); } }}
                            >
                              −
                            </Button>
                            <Typography className="passenger-counter-value" sx={{ color: numChildren > 0 && !isSpecialFare ? '#20202A' : '#AEAEB8' }}>
                              {numChildren}
                            </Typography>
                            <Button
                              className="passenger-counter-btn"
                              disabled={totalPassengers >= 9 || isSpecialFare}
                              onClick={() => { if (!isSpecialFare) { setNumChildren(numChildren + 1); setPassengersTouched(true); } }}
                            >
                              +
                            </Button>
                          </Box>
                        </Box>

                        {/* Infants Section */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '8px', md: '12px' }, opacity: isSpecialFare ? 0.45 : 1 }}>
                          <Typography className="passenger-dropdown-label">
                            Infants (Below 2Ys)
                          </Typography>
                          {isSpecialFare && (
                            <Typography sx={{ fontSize: '0.65rem', color: '#E65100', mt: -0.5, fontFamily: HOST_GROTESK_FAMILY }}>
                              Not available for {fareType === 'student' ? 'Student' : 'Senior Citizen'} fare
                            </Typography>
                          )}
                          <Box className="passenger-counter-bar">
                            <Button
                              className="passenger-counter-btn"
                              disabled={numInfants <= 0 || isSpecialFare}
                              onClick={() => { if (!isSpecialFare) { setNumInfants(numInfants - 1); setPassengersTouched(true); } }}
                            >
                              −
                            </Button>
                            <Typography className="passenger-counter-value" sx={{ color: numInfants > 0 && !isSpecialFare ? '#20202A' : '#AEAEB8' }}>
                              {numInfants}
                            </Typography>
                            <Button
                              className="passenger-counter-btn"
                              disabled={numInfants >= numAdults || totalPassengers >= 9 || isSpecialFare}
                              onClick={() => { if (!isSpecialFare) { setNumInfants(numInfants + 1); setPassengersTouched(true); } }}
                            >
                              +
                            </Button>
                          </Box>
                        </Box>
                      </Box>

                      {totalPassengers >= 9 && (
                        <Typography sx={{ fontSize: '0.75rem', color: '#d32f2f', textAlign: 'center', mt: '12px' }}>
                          Maximum 9 passengers allowed
                        </Typography>
                      )}

                      {/* Done Button */}
                      <Button
                        variant="contained"
                        className="passenger-done-btn"
                        onClick={() => setShowPassengerDropdown(false)}
                      >
                        Done
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
              </>
              )}

              {/* Class Field — same flex as Travelers/Dates; dropdown width is constrained via .class-dropdown-paper (not column width) */}
              <Box sx={getFlightSearchWrappedTailLayout(!hideTripTypeSelector)}>
              <Box className="flight-search-strip-class-field" sx={{
                flex: { xs: '1 1 100%', sm: '1 1 100%', md: '0 0 auto', lg: '0 0 7.75rem' },
                minWidth: { xs: '100%', sm: '100%', md: '88px', lg: '7.75rem' },
                width: { md: 'auto', lg: '7.75rem' },
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible',
                pt: { xs: '10px', sm: '10px', md: 0 },
                pb: { xs: 0, sm: 0, md: 0 },
                pl: { xs: '4px', sm: '4px', md: 0 },
                borderBottom: { xs: 'none', sm: 'none', md: 'none' }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: -0.5, ml: 0 }}>
                  <Typography sx={{ 
                    color: 'var(--Neutrals-base-black, #20202A)',
                    fontFamily: HOST_GROTESK_FAMILY,
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '20px',
                    letterSpacing: '0.15px',
                    textAlign: 'left',
                    mr: 0.5
                  }}>
                    Class
                  </Typography>
                  <KeyboardArrowDownIcon sx={{ 
                    fontSize: '18px',
                    color: 'var(--Neutrals-base-black, #20202A)',
                    transition: 'transform 0.3s ease',
                    transform: (!isMobile && classDropdownOpen) ? 'rotate(180deg)' : 'rotate(0deg)'
                  }} />
                </Box>
                {isMobile ? (
                  <TextField
                    value={TRAVEL_CLASS_LABELS[travelClass] ?? travelClass}
                    size="small"
                    fullWidth
                    autoComplete="off"
                    onClick={() => { setShowPassengerDropdown(true); }}
                    inputProps={{ readOnly: true, 'aria-label': 'Class', style: { cursor: 'pointer' } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        minHeight: 'unset',
                        height: 'auto',
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        '& fieldset': { border: 'none' },
                        '&.Mui-focused fieldset': { border: 'none', boxShadow: 'none' },
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '1rem',
                        fontWeight: 500,
                        lineHeight: '22px',
                        height: 'auto',
                        padding: '0 !important',
                        color: 'var(--Neutrals-base-black, #20202A) !important',
                        fontFamily: HOST_GROTESK_FAMILY,
                        cursor: 'pointer',
                        paddingLeft: 0,
                      },
                    }}
                  />
                ) : (
                <FormControl
                  size="small"
                  sx={{ width: '100%', maxWidth: '100%', overflow: 'visible' }}
                >
                  <Select
                    value={travelClass}
                    onChange={e => { setTravelClass(e.target.value); setClassTouched(true); }}
                    onOpen={() => setClassDropdownOpen(true)}
                    onClose={() => setClassDropdownOpen(false)}
                    className="class-select-field"
                    inputProps={{ 'aria-label': 'Class' }}
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 500,
                      color: 'var(--Neutrals-base-black, #20202A)',
                      fontFamily: HOST_GROTESK_FAMILY,
                      minHeight: '28px',
                      height: '28px',
                      background: 'transparent',
                      border: 'none',
                      pl: 0,
                      borderRadius: 0,
                      overflow: 'visible',
                      '& fieldset': { border: 'none' },
                      '&.Mui-focused fieldset': { border: 'none', boxShadow: 'none' },
                      '& .MuiSelect-icon': { display: 'none' },
                      '& .MuiOutlinedInput-root': { pl: 0, pr: 0 },
                      '& .MuiSelect-select': {
                        pl: 0,
                        pr: 0,
                        color: 'inherit',
                      },
                    }}
                    MenuProps={{
                      disableScrollLock: true,
                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                      transformOrigin: { vertical: 'top', horizontal: 'left' },
                      PaperProps: {
                        className: 'class-dropdown-paper',
                        style: { minWidth: 'min-content', maxWidth: 'min(100vw - 32px, 220px)' },
                        sx: {
                          background: '#F5F5F7',
                          borderRadius: { xs: '12px', md: '16px' },
                          boxShadow: '0 0 12px rgba(0,0,0,0.12)',
                          border: 'none',
                          p: { xs: '10px 14px', md: '14px 18px' },
                          mt: 1,
                          width: 'max-content',
                          boxSizing: 'border-box',
                          '& .MuiList-root': {
                            p: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: { xs: '10px', md: '14px' },
                          },
                        }
                      }
                    }}
                  >
                    <MenuItem value="ECONOMY" className="class-dropdown-item">Economy</MenuItem>
                    <MenuItem value="BUSINESS" className="class-dropdown-item">Business Class</MenuItem>
                    <MenuItem value="FIRST" className="class-dropdown-item">First Class</MenuItem>
                    <MenuItem value="PREMIUM_ECONOMY" className="class-dropdown-item">Premium Economy</MenuItem>
                  </Select>
                </FormControl>
                )}
              </Box>

              {/* Search Button — desktop / tablet in-strip; mobile CTA is below Special Fares (Figma: fares above, Let’s Go last). */}
              {!isMobile && (
              <Box
                className={
                  hideTripTypeSelector
                    ? 'flight-search-strip-cta-field flight-select-hero-search-btn'
                    : 'flight-search-strip-cta-field'
                }
                sx={{
                flex: { xs: '1 1 100%', md: '0 0 auto' },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignSelf: { md: 'flex-start', lg: 'flex-start' },
                minHeight: { xs: 'auto', md: 'auto' },
                mt: { xs: '8px', md: 0, lg: '4px' },
                px: { xs: 0, sm: 0 }
              }}>
                <Button
                  {...createGtmAttributes(GTM_CLICK_IDS.FLIGHT_SEARCH_LETS_GO)}
                  sx={{
                    background: 'var(--yovo-base-black)',
                    color: 'var(--yovo-base-white)',
                    borderRadius: { xs: '12px', md: '16px' },
                    display: 'flex',
                    height: { xs: '44px', md: '48px' },
                    padding: '6px 20px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                    letterSpacing: '0.4px',
                    fontWeight: 600,
                    fontSize: '14px',
                    fontFamily: 'var(--yovo-font-primary)',
                    textTransform: 'none',
                    width: { xs: '100%', md: 'auto' },
                    '&:hover': { background: '#333' }
                  }}
                  onClick={() => handleSearch()}
                >
                  Let's Go
                </Button>
                
                {/* Test Multi-city API Button */}
                {/* {tripType === 'multicity' && (
                  <Button
                    onClick={testMulticityAPI}
                    sx={{
                      background: '#4CAF50',
                      color: '#fff',
                      borderRadius: 4,
                      display: 'flex',
                      height: '2.5rem',
                      padding: '4px 16px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontWeight: 600,
                      fontSize: '12px',
                      textTransform: 'none',
                      mt: 1,
                      '&:hover': {
                        background: '#45a049',
                      },
                    }}
                  >
                    🧪 Test API
                  </Button>
                )} */}
              </Box>
            )}
              </Box>
            </Box>
            )}

            {/* Multi-city bottom section */}
            {tripType === 'multicity' && (
              <Box sx={{ 
                display: 'flex', 
                gap: { xs: '16px', sm: '16px', md: 'clamp(16px, 2vw, 32px)' }, 
                alignItems: { xs: 'stretch', md: 'center' }, 
                mb: { xs: 0, md: 1 }, 
                width: '100%',
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                /* On mobile (multicity) this section is INSIDE the single Figma card,
                   so it must be transparent and contribute nothing extra to padding. */
                background: { xs: 'transparent', md: '#fff' },
                padding: { xs: 0, sm: 0, md: '5px 10px' },
                mt: { xs: 0, md: 1 },
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: { xs: 'flex-start', md: 'space-between' }
              }}>
                {/* Left side - Passengers and Class */}
                <Box sx={{ 
                  display: 'flex', 
                  /* Wider gap on desktop so labels read clean (matches the design screenshot). */
                  gap: { xs: 0, md: 'clamp(20px, 3vw, 40px)' }, 
                  alignItems: { xs: 'stretch', md: 'center' },
                  flex: { xs: '1 1 100%', md: '1 1 auto' },
                  flexWrap: { xs: 'nowrap', md: 'nowrap' },
                  flexDirection: { xs: 'column', md: 'row' },
                  width: { xs: '100%', md: 'auto' },
                  minWidth: 0
                }}>
                  {/* Travelers Field */}
                  <Box sx={{
                    /* Desktop: natural width (hugs content); Mobile: full width inside card. */
                    flex: { xs: '1 1 100%', md: '0 0 auto' },
                    minWidth: { xs: 0, md: 'clamp(140px, 16vw, 200px)' },
                    display: 'flex',
                    flexDirection: 'column',
                    /* Divider above Travelers on mobile (Figma 3008:105053). */
                    borderTop: { xs: '1px solid var(--neutrals-grey-300, #E8E9ED)', md: 'none' },
                    pt: { xs: '12px', md: 0 }
                  }}>
                  <Typography sx={{ 
                    color: 'var(--Neutrals-base-black, #20202A)',
                    fontFamily: HOST_GROTESK_FAMILY,
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '20px',
                    letterSpacing: '0.15px',
                    mb: -0.5,
                    textAlign: 'left'
                  }}>
                    Travelers
                  </Typography>
                  <Box sx={{ position: 'relative', width: '100%', margin: 0, padding: 0 }} data-passenger-dropdown>
                    <TextField
                      value={passengers}
                      size="small"
                      fullWidth
                      autoComplete="off"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          minHeight: '28px',
                          background: 'transparent',
                          border: 'none',
                          '& fieldset': {
                            border: 'none',
                          },
                          '&.Mui-focused fieldset': {
                            border: 'none',
                            boxShadow: 'none',
                          },
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '1rem',
                          fontWeight: 500,
                          color: '#20202A',
                          fontFamily: HOST_GROTESK_FAMILY,
                          cursor: 'pointer',
                          paddingLeft: 0
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        readOnly: true,
                        'aria-label': 'Travelers',
                        style: { cursor: 'pointer' }
                      }}
                      onClick={() => {
                        if (isSeoHeroDesktopUx) {
                          const opening = seoMcActivePanel !== 'travelers';
                          requestSeoMcPanel(opening ? 'travelers' : null);
                          setShowPassengerDropdown(opening);
                          return;
                        }
                        setShowPassengerDropdown(!showPassengerDropdown);
                      }}
                      readOnly
                    />
                    {showPassengerDropdown && !isMobile && (
                      <Box
                        data-passenger-dropdown
                        className="passenger-dropdown"
                        sx={{
                          position: { xs: 'fixed', md: 'absolute' },
                          top: { xs: '50%', md: '100%' },
                          left: { xs: '50%', md: '50%' },
                          transform: { xs: 'translate(-50%, -50%)', md: 'translateX(-50%)' },
                          zIndex: 1401,
                          isolation: 'isolate',
                          background: '#F5F5F7',
                          border: 'none',
                          borderRadius: { xs: '14px', md: '16px' },
                          boxShadow: '0 0 12px rgba(0,0,0,0.12)',
                          p: { xs: '12px', md: '16px' },
                          pb: { xs: '12px', md: '16px' },
                          pt: { xs: '10px', md: '12px' },
                          mt: { xs: 0, md: 0.5 },
                          minWidth: { xs: '240px', md: '300px' },
                          width: { xs: 'calc(100vw - 64px)', md: 'auto' },
                          maxWidth: { xs: '320px', md: '380px' }
                        }}
                        onClick={e => e.stopPropagation()}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '20px', md: '32px' } }}>
                          {/* Adults Section */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '8px', md: '12px' } }}>
                            <Typography className="passenger-dropdown-label">
                              Adults
                            </Typography>
                            <Box className="passenger-counter-bar">
                              <Button
                                className="passenger-counter-btn"
                                disabled={numAdults <= 1 || numInfants >= numAdults}
                                onClick={() => {
                                  const newAdults = numAdults - 1;
                                  setNumAdults(newAdults);
                                  if (numInfants > newAdults) setNumInfants(newAdults);
                                  setPassengersTouched(true);
                                }}
                              >
                                −
                              </Button>
                              <Typography className="passenger-counter-value" sx={{ color: '#20202A' }}>
                                {numAdults}
                              </Typography>
                              <Button
                                className="passenger-counter-btn"
                                disabled={totalPassengers >= 9}
                                onClick={() => { setNumAdults(numAdults + 1); setPassengersTouched(true); }}
                              >
                                +
                              </Button>
                            </Box>
                          </Box>

                          {/* Children Section */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '8px', md: '12px' }, opacity: isSpecialFare ? 0.45 : 1 }}>
                            <Typography className="passenger-dropdown-label">
                              Children (2Ys - 12Ys)
                            </Typography>
                            {isSpecialFare && (
                              <Typography sx={{ fontSize: '0.65rem', color: '#E65100', mt: -0.5, fontFamily: HOST_GROTESK_FAMILY }}>
                                Not available for {fareType === 'student' ? 'Student' : 'Senior Citizen'} fare
                              </Typography>
                            )}
                            <Box className="passenger-counter-bar">
                              <Button
                                className="passenger-counter-btn"
                              disabled={numChildren <= 0 || isSpecialFare}
                              onClick={() => { if (!isSpecialFare) { setNumChildren(numChildren - 1); setPassengersTouched(true); } }}
                            >
                              −
                            </Button>
                            <Typography className="passenger-counter-value" sx={{ color: numChildren > 0 && !isSpecialFare ? '#20202A' : '#AEAEB8' }}>
                              {numChildren}
                            </Typography>
                            <Button
                              className="passenger-counter-btn"
                              disabled={totalPassengers >= 9 || isSpecialFare}
                              onClick={() => { if (!isSpecialFare) { setNumChildren(numChildren + 1); setPassengersTouched(true); } }}
                              >
                                +
                              </Button>
                            </Box>
                          </Box>

                          {/* Infants Section */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '8px', md: '12px' }, opacity: isSpecialFare ? 0.45 : 1 }}>
                            <Typography className="passenger-dropdown-label">
                              Infants (Below 2Ys)
                            </Typography>
                            {isSpecialFare && (
                              <Typography sx={{ fontSize: '0.65rem', color: '#E65100', mt: -0.5, fontFamily: HOST_GROTESK_FAMILY }}>
                                Not available for {fareType === 'student' ? 'Student' : 'Senior Citizen'} fare
                              </Typography>
                            )}
                            <Box className="passenger-counter-bar">
                              <Button
                                className="passenger-counter-btn"
                                disabled={numInfants <= 0 || isSpecialFare}
                                onClick={() => { if (!isSpecialFare) { setNumInfants(numInfants - 1); setPassengersTouched(true); } }}
                              >
                                −
                              </Button>
                              <Typography className="passenger-counter-value" sx={{ color: numInfants > 0 && !isSpecialFare ? '#20202A' : '#AEAEB8' }}>
                                {numInfants}
                              </Typography>
                              <Button
                                className="passenger-counter-btn"
                                disabled={numInfants >= numAdults || totalPassengers >= 9 || isSpecialFare}
                                onClick={() => { if (!isSpecialFare) { setNumInfants(numInfants + 1); setPassengersTouched(true); } }}
                              >
                                +
                              </Button>
                            </Box>
                          </Box>
                        </Box>

                        {totalPassengers >= 9 && (
                          <Typography sx={{ fontSize: '0.75rem', color: '#d32f2f', textAlign: 'center', mt: '12px' }}>
                            Maximum 9 passengers allowed
                          </Typography>
                        )}

                        {/* Done Button */}
                        <Button
                          variant="contained"
                          className="passenger-done-btn"
                          onClick={() => {
                            if (isSeoHeroDesktopUx) requestSeoMcPanel(null);
                            setShowPassengerDropdown(false);
                          }}
                        >
                          Done
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>

                  {/* Class Field */}
                  <Box className="flight-search-strip-class-field" sx={{
                    flex: { xs: '1 1 100%', md: '0 0 auto' },
                    minWidth: { xs: 0, md: 'clamp(120px, 12vw, 160px)' },
                    width: { xs: '100%', md: 'auto' },
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'visible',
                    borderTop: { xs: '1px solid var(--neutrals-grey-300, #E8E9ED)', md: 'none' },
                    pt: { xs: '12px', md: 0 },
                    pb: { xs: 0, md: 0 },
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: -0.5, ml: 0 }}>
                      <Typography sx={{ 
                        color: 'var(--Neutrals-base-black, #20202A)',
                        fontFamily: HOST_GROTESK_FAMILY,
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '20px',
                        letterSpacing: '0.15px',
                        textAlign: 'left',
                        mr: 0.5
                      }}>
                        Class
                      </Typography>
                      <KeyboardArrowDownIcon sx={{ 
                        fontSize: '18px',
                        color: 'var(--Neutrals-base-black, #20202A)',
                        transition: 'transform 0.3s ease',
                        transform: (!isMobile && classDropdownOpen) ? 'rotate(180deg)' : 'rotate(0deg)'
                      }} />
                    </Box>
                  {isMobile ? (
                    <TextField
                      value={TRAVEL_CLASS_LABELS[travelClass] ?? travelClass}
                      size="small"
                      fullWidth
                      autoComplete="off"
                      onClick={() => { setShowPassengerDropdown(true); }}
                      inputProps={{ readOnly: true, 'aria-label': 'Class', style: { cursor: 'pointer' } }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          minHeight: 'unset',
                          height: 'auto',
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          '& fieldset': { border: 'none' },
                          '&.Mui-focused fieldset': { border: 'none', boxShadow: 'none' },
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '1rem',
                          fontWeight: 500,
                          lineHeight: '22px',
                          height: 'auto',
                          padding: '0 !important',
                          color: 'var(--Neutrals-base-black, #20202A) !important',
                          fontFamily: HOST_GROTESK_FAMILY,
                          cursor: 'pointer',
                          paddingLeft: 0,
                        },
                      }}
                    />
                  ) : (
                  <FormControl
                    size="small"
                    sx={{ width: '100%', maxWidth: '100%', overflow: 'visible' }}
                  >
                    <Select
                      value={travelClass}
                      onChange={e => { setTravelClass(e.target.value); setClassTouched(true); }}
                      open={isSeoHeroDesktopUx ? seoMcActivePanel === 'class' : undefined}
                      onOpen={() => {
                        if (isSeoHeroDesktopUx) requestSeoMcPanel('class');
                        setClassDropdownOpen(true);
                      }}
                      onClose={() => {
                        setClassDropdownOpen(false);
                        if (isSeoHeroDesktopUx) requestSeoMcPanel(null);
                      }}
                      className="class-select-field"
                    inputProps={{ 'aria-label': 'Class' }}
                      sx={{
                        fontSize: '1rem',
                        fontWeight: 500,
                        color: 'var(--Neutrals-base-black, #20202A)',
                        fontFamily: HOST_GROTESK_FAMILY,
                        minHeight: '28px',
                        height: '28px',
                        background: 'transparent',
                        border: 'none',
                        pl: 0,
                        borderRadius: 0,
                        overflow: 'visible',
                        '& fieldset': { border: 'none' },
                        '&.Mui-focused fieldset': { border: 'none', boxShadow: 'none' },
                        '& .MuiSelect-icon': { display: 'none' },
                        '& .MuiOutlinedInput-root': { pl: 0, pr: 0 },
                        '& .MuiSelect-select': {
                          pl: 0,
                          pr: 0,
                          color: 'inherit',
                        },
                      }}
                      MenuProps={{
                        disableScrollLock: true,
                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                        PaperProps: {
                          className: 'class-dropdown-paper',
                          style: { minWidth: 'min-content', maxWidth: 'min(100vw - 32px, 220px)' },
                          sx: {
                            background: '#F5F5F7',
                            borderRadius: { xs: '12px', md: '16px' },
                            boxShadow: '0 0 12px rgba(0,0,0,0.12)',
                            border: 'none',
                            p: { xs: '10px 14px', md: '14px 18px' },
                            mt: 1,
                            width: 'max-content',
                            boxSizing: 'border-box',
                            '& .MuiList-root': {
                              p: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: { xs: '10px', md: '14px' },
                            },
                          }
                        }
                      }}
                    >
                      <MenuItem value="ECONOMY" className="class-dropdown-item">Economy</MenuItem>
                      <MenuItem value="BUSINESS" className="class-dropdown-item">Business Class</MenuItem>
                      <MenuItem value="FIRST" className="class-dropdown-item">First Class</MenuItem>
                      <MenuItem value="PREMIUM_ECONOMY" className="class-dropdown-item">Premium Economy</MenuItem>
                    </Select>
                  </FormControl>
                  )}
                  </Box>
                </Box>

                {/* Search Button - right side on md+; mobile CTA is after Special Fares (see below). */}
                {!isMobile && (
                <Box sx={{ 
                  flex: { xs: '1 1 100%', md: '0 0 auto' },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: { xs: 'stretch', md: 'flex-end' },
                  minHeight: { xs: 'auto', md: 'auto' },
                  marginLeft: { xs: 0, md: 'auto' },
                  mt: { xs: '4px', md: 0 },
                  flexShrink: 0
                }}>
                  <Button
                    {...createGtmAttributes(GTM_CLICK_IDS.FLIGHT_SEARCH_LETS_GO)}
                    sx={{
                      background: 'var(--yovo-base-black)',
                      color: 'var(--yovo-base-white)',
                      borderRadius: { xs: '12px', md: '16px' },
                      display: 'flex',
                      height: { xs: '44px', md: '48px' },
                      px: { xs: '20px', md: 'clamp(20px, 2.5vw, 36px)' },
                      py: '6px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '10px',
                      letterSpacing: '0.4px',
                      fontWeight: 600,
                      fontSize: { xs: '14px', md: 'clamp(14px, 1vw, 16px)' },
                      fontFamily: 'var(--yovo-font-primary)',
                      textTransform: 'none',
                      width: { xs: '100%', md: 'auto' },
                      minWidth: { xs: 'auto', md: '120px' },
                      whiteSpace: 'nowrap',
                      boxShadow: 'none',
                      '&:hover': { background: '#333', boxShadow: 'none' }
                    }}
                    onClick={() => handleSearch()}
                  >
                    Let's Go
                  </Button>
                </Box>
                )}
              </Box>
            )}
            </Box>{/* end white inner box */}

            {/* Special Fares: mobile — horizontal slider cards (title + subtitle stacked). Tablet+ — full row with radios. */}
            {isMobile ? (
              <Box
                className="special-fares-slider-wrap"
                sx={{
                  mt: 0.75,
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  className="special-fares-options special-fares-slider"
                  sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: '8px',
                    flexWrap: 'nowrap',
                    width: '100%',
                    minWidth: 0,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehaviorX: 'contain',
                    pb: '4px',
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                  }}
                >
                  {SPECIAL_FARE_KEYS.map((fareKey) => {
                    const fare = SPECIAL_FARE_META[fareKey];
                    const handler =
                      fareKey === 'regular'
                        ? () => setFareType('regular')
                        : () => handleFareTypeCheckbox(fareKey);
                    const isActive = fareType === fareKey;
                    const pillDisabled =
                      fareKey !== 'regular' && fareKey !== 'armedForces' && hasChildrenOrInfants;
                    const pill = (
                    <Box
                      key={fareKey}
                      role="button"
                      tabIndex={pillDisabled ? -1 : 0}
                      {...createGtmAttributes(GTM_CLICK_IDS.FLIGHT_SEARCH_SPECIAL_FARE, {
                        [GTM_CONTEXT_ATTRS.FARE]: fareKey,
                      })}
                      className={
                        isSeoEmbeddedSpecialFares
                          ? `fare-option-pill${isActive ? ' fare-option-pill--active' : ''}`
                          : undefined
                      }
                      onClick={pillDisabled ? undefined : handler}
                      onKeyDown={(e) => { if (!pillDisabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handler(); } }}
                      sx={{
                        flex: '0 0 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        scrollSnapAlign: 'start',
                        border: isActive ? '1.5px solid var(--yovo-grey-800)' : '1px solid var(--yovo-grey-300)',
                        background: isActive ? 'var(--yovo-grey-200)' : 'var(--yovo-grey-100)',
                        borderRadius: '10px',
                        px: 1.5,
                        py: 0.75,
                        minWidth: '120px',
                        cursor: pillDisabled ? 'not-allowed' : 'pointer',
                        opacity: pillDisabled ? 0.45 : 1,
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          width: '100%',
                          gap: 0.25,
                        }}
                      >
                        <Typography sx={{
                          fontFamily: 'var(--yovo-font-primary)',
                          fontSize: '0.8125rem',
                          fontWeight: isActive ? 700 : 500,
                          lineHeight: '18px',
                          color: 'var(--yovo-base-black)',
                        }}>
                          {fare.title}
                        </Typography>
                        <SpecialFareInfoIcon fareKey={fareKey} onOpen={openSpecialFareInfo} />
                      </Box>
                      <Typography sx={{
                        fontFamily: 'var(--yovo-font-primary)',
                        fontSize: '0.6875rem',
                        fontWeight: 400,
                        lineHeight: '16px',
                        color: 'var(--yovo-grey-700)',
                        whiteSpace: 'nowrap',
                      }}>
                        {fare.subtitle}
                      </Typography>
                    </Box>
                    );
                    return wrapSpecialFarePill(fareKey, pill, {
                      disabled: pillDisabled,
                      showInfoTooltip: false,
                    });
                  })}
                </Box>
              </Box>
            ) : (
              <Box className="special-fares-row" sx={{
                display: 'flex',
                alignItems: { sm: 'flex-start', md: 'center' },
                gap: { sm: '10px', md: '16px' },
                mt: { sm: 1.5 },
                pt: { sm: 1 },
                pl: { sm: 0, md: '36px' },
                flexDirection: { sm: 'column', md: 'row' },
                flexWrap: 'nowrap',
                maxWidth: '100%',
                overflow: 'hidden',
              }}
              >
                <Typography sx={{
                  color: 'var(--yovo-base-black)',
                  fontFamily: 'var(--yovo-font-primary)',
                  fontSize: { sm: '14px', md: '16px' },
                  fontWeight: 600,
                  lineHeight: '24px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
                >
                  Special Fares
                </Typography>
                <Box className="special-fares-options" sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { sm: '8px', md: '0px' },
                  flexWrap: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                  width: { sm: '100%', md: 'auto' },
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  justifyContent: { sm: 'flex-start', md: 'flex-start' },
                  pb: { sm: '4px', md: 0 },
                }}
                >
                  {SPECIAL_FARE_KEYS.map((fareKey) => {
                    const fare = SPECIAL_FARE_META[fareKey];
                    const handler =
                      fareKey === 'regular'
                        ? () => setFareType('regular')
                        : () => handleFareTypeCheckbox(fareKey);
                    const isActive = fareType === fareKey;
                    const pillDisabled =
                      fareKey !== 'regular' && fareKey !== 'armedForces' && hasChildrenOrInfants;
                    const pill = (
                    <Box
                      key={fareKey}
                      {...createGtmAttributes(GTM_CLICK_IDS.FLIGHT_SEARCH_SPECIAL_FARE, {
                        [GTM_CONTEXT_ATTRS.FARE]: fareKey,
                      })}
                      className={
                        isSeoEmbeddedSpecialFares && isActive
                          ? 'fare-option-pill fare-option-pill--active'
                          : 'fare-option-pill'
                      }
                      onClick={pillDisabled ? undefined : handler}
                      sx={{
                        border: isActive ? '1px solid var(--yovo-grey-800)' : '1px solid transparent',
                        background: isActive ? 'var(--yovo-grey-200)' : 'transparent',
                        cursor: pillDisabled ? 'not-allowed' : 'pointer',
                        opacity: pillDisabled ? 0.45 : 1,
                      }}
                    >
                      <Box
                        component="img"
                        src={isActive ? radioButtonFilled : radioButtonEmpty}
                        alt=""
                        className="fare-option-radio"
                      />
                      <Box>
                        <Typography className="fare-option-title" sx={{ fontWeight: isActive ? 600 : 500 }}>{fare.title}</Typography>
                        <Typography className="fare-option-subtitle" sx={{ color: 'var(--yovo-grey-700)' }}>{fare.subtitle}</Typography>
                      </Box>
                    </Box>
                    );
                    return wrapSpecialFarePill(fareKey, pill, {
                      disabled: pillDisabled,
                      showInfoTooltip: true,
                    });
                  })}
                </Box>
                {showHomepageSearchPromo && (
                  <ConvenienceFeeHomepageSearchBanner variant="desktop" />
                )}
                {/* Flight Tracker temporarily hidden
                <Button
                  className="flight-tracker-btn"
                  sx={{
                    background: 'var(--yovo-grey-200)',
                    border: 'none',
                    borderRadius: '12px',
                    padding: { sm: '10px 16px', md: '12px 16px' },
                    color: 'var(--yovo-base-black)',
                    fontFamily: 'var(--yovo-font-primary)',
                    fontSize: { sm: '13px', md: '14px' },
                    fontWeight: 600,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    lineHeight: '20px',
                    letterSpacing: '0.4px',
                    ml: { sm: 0, md: 'auto' },
                    minWidth: { sm: 'auto', md: '150px' },
                    height: { sm: '44px', md: '48px' },
                    flexShrink: 0,
                    '&:hover': {
                      background: 'var(--yovo-base-black)',
                      color: 'var(--yovo-base-white)',
                    },
                  }}
                  onClick={() => {}}
                >
                  Flight Tracker
                </Button>
                */}
              </Box>
            )}

            {/* Mobile (Figma 2819 / 3008): Special Fares above full-width CTA; strip/card above has no duplicate button. */}
            {isMobile && (
              <Box
                className="flight-form-mobile-lets-go"
                sx={{
                  width: '100%',
                  mt: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  boxSizing: 'border-box',
                }}
              >
                <Button
                  type="button"
                  {...createGtmAttributes(GTM_CLICK_IDS.FLIGHT_SEARCH_LETS_GO)}
                  sx={{
                    background: 'var(--yovo-base-black)',
                    color: 'var(--yovo-base-white)',
                    borderRadius: '12px',
                    display: 'flex',
                    height: '48px',
                    px: '20px',
                    py: '6px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '10px',
                    letterSpacing: '0.4px',
                    fontWeight: 600,
                    fontSize: '14px',
                    fontFamily: 'var(--yovo-font-primary)',
                    textTransform: 'none',
                    width: '100%',
                    boxShadow: 'none',
                    '&:hover': { background: '#333', boxShadow: 'none' },
                  }}
                  onClick={() => handleSearch()}
                >
                  Let's Go
                </Button>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      <MountWhenOpened open={showFareTypeModal}>
        <SpecialFareNoticeDialog
        open={showFareTypeModal}
          fareType={fareType}
        onClose={() => setShowFareTypeModal(false)}
          onCancel={() => {
              setShowFareTypeModal(false);
              setFareType('regular');
            }}
          onConfirm={() => {
              setShowFareTypeModal(false);
              handleSearch(true);
            }}
        />
      </MountWhenOpened>

      {/* Mobile: special fare (i) → centered white info popover */}
      {isMobile && specialFareInfoKey && SPECIAL_FARE_META[specialFareInfoKey]?.tooltip && (
        <Suspense fallback={null}>
          <SpecialFareInfoSheet fareKey={specialFareInfoKey} onClose={closeSpecialFareInfo} />
        </Suspense>
      )}

      {/* ── Mobile Airport Bottom Sheet ─────────────────────────────────────────
          Portal mounts only while open (same as travellers/date sheets) so the closed
          sheet cannot peek on Android during page scroll. Search logic unchanged.
          ────────────────────────────────────────────────────────────────────── */}
      <MobileInteractionHost
        ref={mobileHostRef}
        renderAirport={(field, hosted) => (
        <Portal>
          {/* Backdrop */}
          <Box
            className="mobile-airport-sheet-backdrop open"
            onClick={closeMobileAirportSheet}
          />

          {/* Sheet */}
          <Box className="mobile-airport-sheet open">
            {/* Drag handle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: '10px', pb: '2px' }}>
              <Box sx={{ width: '36px', height: '4px', borderRadius: '2px', background: '#D0D0D8' }} />
            </Box>

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', px: '16px', pt: '14px', pb: '10px' }}>
              <Typography sx={{ flex: 1, fontFamily: HOST_GROTESK_FAMILY, fontSize: '16px', fontWeight: 600, color: '#1A1827', lineHeight: '22px' }}>
                Search cities or airport
              </Typography>
              <Box
                onClick={closeMobileAirportSheet}
                sx={{ p: '6px', cursor: 'pointer', WebkitTapHighlightColor: 'transparent', ml: '8px' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="#20202A" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
              </Box>
            </Box>

            {/* Origin input */}
            <Box sx={{ px: '16px', pb: '8px' }}>
              {(() => {
                const mcSeg = mobileSheetMcSegmentId != null
                  ? multiCitySegments.find(s => s.id === mobileSheetMcSegmentId)
                  : null;
                const shownAirport = mcSeg ? mcSeg.fromAirport : fromAirport;
                const isActive = field === 'from';
                const selected = formatMobileSheetSelectedAirport(shownAirport);
                const showSelected = Boolean(selected && !isActive);
                return (
              <Box
                className={`mobile-sheet-field${isActive ? ' active' : ''}`}
                sx={MOBILE_AIRPORT_SHEET_FIELD_SX(isActive)}
                onClick={() => { mobileSheetFieldRef.current = 'from'; mobileHostRef.current?.openAirport('from'); }}
              >
                <Box className="mobile-sheet-field-inner">
                  <Typography className="mobile-sheet-field-label" component="span">From</Typography>
                  {showSelected ? (
                    <Box className="mobile-sheet-field-selected">
                      <Typography component="span" className="mobile-sheet-field-code-city">{selected.codeCity}</Typography>
                      {selected.airportName ? (
                        <Typography component="span" className="mobile-sheet-field-airport-name">{selected.airportName}</Typography>
                      ) : null}
                    </Box>
                  ) : (
                  <input
                    ref={mobileFromInputRef}
                    type="text"
                    value={fromInput}
                    placeholder={mobileSheetMcSegmentId != null ? MULTICITY_FROM_PLACEHOLDER : 'Search city or airport'}
                    className="mobile-sheet-input"
                    onChange={(e) => {
                      const v = e.target.value;
                      setFromInput(v);
                      if (mobileSheetMcSegmentId != null) {
                        if (!v) setFromAirport(null);
                        setMultiCitySegments((prev) => prev.map((s) => {
                          if (s.id !== mobileSheetMcSegmentId) return s;
                          if (!v) return { ...s, fromInput: '', fromAirport: null, fromSearchQuery: '' };
                          return { ...s, fromInput: v };
                        }));
                        if (!v && isMobile && mobileSheetOpen) loadIndiaAirportDefaults('from');
                      } else if (!v) {
                        setFromAirport(null);
                        if (isMobile && mobileSheetOpen) loadIndiaAirportDefaults('from');
                      }
                    }}
                    onFocus={() => { mobileSheetFieldRef.current = 'from'; mobileHostRef.current?.openAirport('from'); }}
                  />
                  )}
                </Box>
                {!showSelected && (fromInput.trim() || shownAirport) ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ml: '6px', alignSelf: 'center' }}>
                      <button
                        type="button"
                        className="mobile-sheet-field-clear"
                        aria-label="Clear origin"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          clearMobileSheetField('from');
                        }}
                        onMouseDown={(ev) => ev.preventDefault()}
                      >
                        <CloseIcon sx={{ fontSize: '1.125rem' }} aria-hidden />
                      </button>
                    </Box>
                ) : null}
              </Box>
                );
              })()}
            </Box>

            {/* Destination input */}
            <Box sx={{ px: '16px', pb: '12px' }}>
              {(() => {
                const mcSeg = mobileSheetMcSegmentId != null
                  ? multiCitySegments.find(s => s.id === mobileSheetMcSegmentId)
                  : null;
                const shownAirport = mcSeg ? mcSeg.toAirport : toAirport;
                const isActive = field === 'to';
                const selected = formatMobileSheetSelectedAirport(shownAirport);
                const showSelected = Boolean(selected && !isActive);
                return (
              <Box
                className={`mobile-sheet-field${isActive ? ' active' : ''}`}
                sx={MOBILE_AIRPORT_SHEET_FIELD_SX(isActive)}
                onClick={() => { mobileSheetFieldRef.current = 'to'; mobileHostRef.current?.openAirport('to'); }}
              >
                <Box className="mobile-sheet-field-inner">
                  <Typography className="mobile-sheet-field-label" component="span">To</Typography>
                  {showSelected ? (
                    <Box className="mobile-sheet-field-selected">
                      <Typography component="span" className="mobile-sheet-field-code-city">{selected.codeCity}</Typography>
                      {selected.airportName ? (
                        <Typography component="span" className="mobile-sheet-field-airport-name">{selected.airportName}</Typography>
                      ) : null}
                    </Box>
                  ) : (
                  <input
                    ref={mobileToInputRef}
                    type="text"
                    value={toInput}
                    placeholder={mobileSheetMcSegmentId != null ? MULTICITY_TO_PLACEHOLDER : 'Search city or airport'}
                    className="mobile-sheet-input"
                    onChange={(e) => {
                      const v = e.target.value;
                      setToInput(v);
                      if (mobileSheetMcSegmentId != null) {
                        if (!v) setToAirport(null);
                        setMultiCitySegments((prev) => prev.map((s) => {
                          if (s.id !== mobileSheetMcSegmentId) return s;
                          if (!v) return { ...s, toInput: '', toAirport: null, toSearchQuery: '' };
                          return { ...s, toInput: v };
                        }));
                        if (!v && isMobile && mobileSheetOpen) loadIndiaAirportDefaults('to');
                      } else if (!v) {
                        setToAirport(null);
                        if (isMobile && mobileSheetOpen) loadIndiaAirportDefaults('to');
                      }
                    }}
                    onFocus={() => { mobileSheetFieldRef.current = 'to'; mobileHostRef.current?.openAirport('to'); }}
                  />
                  )}
                </Box>
                {!showSelected && (toInput.trim() || shownAirport) ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ml: '6px', alignSelf: 'center' }}>
                      <button
                        type="button"
                        className="mobile-sheet-field-clear"
                        aria-label="Clear destination"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          clearMobileSheetField('to');
                        }}
                        onMouseDown={(ev) => ev.preventDefault()}
                      >
                        <CloseIcon sx={{ fontSize: '1.125rem' }} aria-hidden />
                      </button>
                    </Box>
                ) : null}
              </Box>
                );
              })()}
            </Box>

            {/* Results list */}
            <Box className="mobile-sheet-results">
              {/* Loading state */}
              {(hosted.loading && !(field === 'from' ? fromOptions : toOptions).length) && (
                <Box sx={{ px: '20px', py: '16px', color: 'var(--yovo-grey-700)', fontFamily: HOST_GROTESK_FAMILY, fontSize: '14px' }}>
                  Searching airports...
                </Box>
              )}

              {/* Airport options */}
              {!(hosted.loading && !(field === 'from' ? fromOptions : toOptions).length) &&
                (hosted.options.length ? hosted.options : (field === 'from' ? fromOptions : toOptions)).map((option, index) => {
                  if (option.isSeparator) {
                    return (
                      <Box key={`sep-${index}`} className="mobile-sheet-separator">
                        {option.label}
                      </Box>
                    );
                  }

                  const cityName = option.isNearestAirport
                    ? option.address?.cityName || option.cityName
                    : (option.cityName || option.address?.cityName);
                  const countryName = option.isNearestAirport
                    ? option.address?.countryName || option.countryName
                    : (option.countryName || option.address?.countryName || 'India');
                  const airportName = option.name || '';
                  const displayCity = cityName ? `${cityName}, ${countryName}` : countryName;

                  return (
                    <Box
                      key={option.iataCode || index}
                      className="mobile-sheet-option"
                      sx={{ pl: option.isNearestAirport ? '43px' : '16px' }}
                      onPointerDown={(e) => e.preventDefault()} // prevent input blur before selection
                      onClick={() => handleMobileAirportSelect(option)}
                    >
                      <img src={flight_icon_homepage} className="flight-dropdown-option-icon" alt="" />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography className="flight-dropdown-option-city">{displayCity}</Typography>
                        <Typography className="flight-dropdown-option-airport">
                          {option.isNearestAirport
                            ? `${option.distanceKm || option.distanceFromCity || ''} KMs from ${option.isNearestTo || cityName || ''}`
                            : airportName
                          }
                        </Typography>
                      </Box>
                      <Typography sx={{ fontFamily: HOST_GROTESK_FAMILY, fontSize: '14px', fontWeight: 600, color: '#434151', flexShrink: 0, ml: '8px', mt: '2px', letterSpacing: '0.0125em' }}>
                        {option.iataCode}
                      </Typography>
                    </Box>
                  );
                })
              }
            </Box>
          </Box>
        </Portal>
      )}
        renderDate={(draft) => (
          <MobileSearchDateSheet
            open
            onClose={() => {
              draft.close();
              setShowDatePicker(false);
            }}
            onSave={() => {
              const picked = fareSelectionRef.current;
              const start = picked && 'start' in picked ? picked.start : draft.start;
              const end = picked && 'start' in picked ? picked.end : draft.end;
              if (!start) return;
              if (tripType === 'roundtrip' && !end) return;
              closeThenRun(draft.close, () => {
                setStartDate(start);
                setEndDate(tripType === 'roundtrip' ? end : null);
                setMobileDateDraftStart(start);
                setMobileDateDraftEnd(end);
                setShowDatePicker(false);
              });
            }}
            tripType={tripType === 'roundtrip' ? 'roundtrip' : 'oneway'}
            dateDraftStart={draft.start}
            dateDraftEnd={draft.end}
            dateSegmentFocused={draft.field}
            onDateSegmentFocus={(nextField) => mobileHostRef.current?.setDateField(nextField)}
            onClearDeparture={() => draft.setStart(null)}
            onClearReturn={() => draft.setEnd(null)}
          >
            {renderMobileFullscreenMainCalendars(draft)}
          </MobileSearchDateSheet>
        )}
      />

      {/* Mobile: Travellers & Class — full-screen modal (viewport under 768px); same state as desktop dropdown */}
      {isMobile && showPassengerDropdown && (
        <Portal>
          <Box
            className="mobile-travellers-sheet-backdrop open"
            data-mobile-travellers-class-sheet
            onClick={() => setShowPassengerDropdown(false)}
            aria-hidden
          />
          <Box
            className="mobile-travellers-sheet open"
            data-mobile-travellers-class-sheet
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-travellers-class-title"
          >
            <Box className="mobile-travellers-sheet-header">
              <IconButton
                type="button"
                size="small"
                onClick={() => setShowPassengerDropdown(false)}
                aria-label="Close"
                sx={{ color: '#20202A', ml: -0.5, flexShrink: 0 }}
              >
                <CloseIcon sx={{ fontSize: 22 }} />
              </IconButton>
              <Typography
                id="mobile-travellers-class-title"
                component="h2"
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  fontFamily: `${HOST_GROTESK_FAMILY}, sans-serif`,
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  color: '#20202A',
                  lineHeight: 1.3,
                }}
              >
                Travellers and Class
              </Typography>
              <Box sx={{ width: 40, flexShrink: 0 }} aria-hidden />
            </Box>

            <Box className="mobile-travellers-sheet-scroll">
              <Typography className="mobile-travellers-sheet-section-label" component="p">
                Select traveller
              </Typography>
              <Box className="mobile-travellers-section">
                <Box className="mobile-travellers-section-head">
                  <span className="mobile-travellers-section-title">Adults</span>
                  <span className="mobile-travellers-section-hint">12yrs above</span>
                </Box>
                <Box className="mobile-travellers-num-row" role="group" aria-label="Adults">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => {
                    const inf = Math.min(numInfants, n);
                    const disabled = n + numChildren + inf > 9;
                    return (
                      <button
                        key={`adult-${n}`}
                        type="button"
                        className={`mobile-travellers-num-chip${numAdults === n ? ' mobile-travellers-num-chip--active' : ''}`}
                        disabled={disabled}
                        onClick={() => pickMobileAdults(n)}
                      >
                        {n}
                      </button>
                    );
                  })}
                </Box>
              </Box>

              <Box className="mobile-travellers-section" sx={{ opacity: isSpecialFare ? 0.45 : 1 }}>
                <Box className="mobile-travellers-section-head">
                  <span className="mobile-travellers-section-title">Children</span>
                  <span className="mobile-travellers-section-hint">2-12yrs</span>
                </Box>
                {isSpecialFare && (
                  <Typography sx={{ fontSize: '0.65rem', color: '#E65100', mb: 0.5, fontFamily: HOST_GROTESK_FAMILY }}>
                    Not available for {fareType === 'student' ? 'Student' : 'Senior Citizen'} fare
                  </Typography>
                )}
                <Box className="mobile-travellers-num-row" role="group" aria-label="Children">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((c) => {
                    const disabled = isSpecialFare ? c !== 0 : numAdults + c + numInfants > 9;
                    return (
                      <button
                        key={`ch-${c}`}
                        type="button"
                        className={`mobile-travellers-num-chip${numChildren === c ? ' mobile-travellers-num-chip--active' : ''}`}
                        disabled={disabled}
                        onClick={() => pickMobileChildren(c)}
                      >
                        {c}
                      </button>
                    );
                  })}
                </Box>
              </Box>

              <Box className="mobile-travellers-section" sx={{ opacity: isSpecialFare ? 0.45 : 1 }}>
                <Box className="mobile-travellers-section-head">
                  <span className="mobile-travellers-section-title">Infants</span>
                  <span className="mobile-travellers-section-hint">Below 2yrs</span>
                </Box>
                {isSpecialFare && (
                  <Typography sx={{ fontSize: '0.65rem', color: '#E65100', mb: 0.5, fontFamily: HOST_GROTESK_FAMILY }}>
                    Not available for {fareType === 'student' ? 'Student' : 'Senior Citizen'} fare
                  </Typography>
                )}
                <Box className="mobile-travellers-num-row" role="group" aria-label="Infants">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => {
                    const disabled = isSpecialFare ? i !== 0 : (i > numAdults || numAdults + numChildren + i > 9);
                    return (
                      <button
                        key={`inf-${i}`}
                        type="button"
                        className={`mobile-travellers-num-chip${numInfants === i ? ' mobile-travellers-num-chip--active' : ''}`}
                        disabled={disabled}
                        onClick={() => pickMobileInfants(i)}
                      >
                        {i}
                      </button>
                    );
                  })}
                </Box>
              </Box>

              <Box className="mobile-travellers-section">
                <span className="mobile-travellers-class-heading">Class</span>
                <Box className="mobile-travellers-class-wrap">
                  {MOBILE_CLASS_OPTIONS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`mobile-travellers-class-chip${travelClass === value ? ' mobile-travellers-class-chip--active' : ''}`}
                      onClick={() => { setTravelClass(value); setClassTouched(true); }}
                    >
                      {label}
                    </button>
                  ))}
                </Box>
              </Box>

              {totalPassengers >= 9 && (
                <Typography sx={{ fontSize: '0.75rem', color: '#d32f2f', textAlign: 'center', mt: 1 }}>
                  Maximum 9 passengers allowed
                </Typography>
              )}
            </Box>

            <Box className="mobile-travellers-sheet-footer">
              <Box className="mobile-travellers-sheet-footer-summary">
                <Typography component="div" className="mobile-travellers-sheet-footer-line1">
                  {totalPassengers} Guest{totalPassengers !== 1 ? 's' : ''} • {TRAVEL_CLASS_LABELS[travelClass] ?? travelClass}
                </Typography>
                <Typography component="div" className="mobile-travellers-sheet-footer-line2">
                  {travellersSheetFooterDate}
                </Typography>
              </Box>
              <Button
                type="button"
                variant="contained"
                className="mobile-travellers-sheet-save"
                onClick={() => setShowPassengerDropdown(false)}
              >
                Save
              </Button>
            </Box>
          </Box>
        </Portal>
      )}

      {/* Mobile date sheet is opened by MobileInteractionHost so the tap does not re-render this form. */}
    </Box>
  );
};

// Memoize FlightForm to prevent unnecessary re-renders when Homepage re-renders
// but props haven't changed
export default React.memo(FlightForm);
